/**
 * buildDecorations — scan line-by-line and produce all CodeMirror decorations.
 *
 * This is the core decoration builder that creates L0 (pretty widgets),
 * L1 (hover hints), and L2 (raw syntax) decorations for Magic MD syntax.
 */

import { RangeSetBuilder } from "@codemirror/state";
import { foldedRanges } from "@codemirror/language";
import {
    Decoration,
    type DecorationSet,
    type EditorView,
} from "@codemirror/view";
import { t as translate } from "@magic-md/i18n";
import type {
    ExecutionStatus,
    ForExecutionStatus,
} from "../../store/plugin-execution-store";
import { usePluginExecutionStore } from "../../store/plugin-execution-store";

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

import {
    ContentButtonGroupWidget,
    PrettyContentDefineWidget,
} from "./content/widgets";
import { PrettyHtmlCommentWidget } from "./comment/widgets";
import {
    ForButtonGroupWidget,
    PrettyForWidget,
} from "./control-flow/for-widget";
import { defineCloseMark, defineHeadMark } from "./define/marks";
import {
    ButtonGroupWidget,
    ErrorMessageWidget,
    PrettyDefineWidget,
} from "./define/widgets";
import { PrettyFenceWidget } from "./fence/widgets";
import { FenceBodyWidget } from "./fence/body-widget";
import { ImportButtonGroupWidget, PrettyImportWidget } from "./import/widgets";
import {
    collectMarkdownReferenceDefinitions,
    findMarkdownLinkSyntaxMatches,
    parseMarkdownReferenceDefinitionSyntax,
    rangeContainsMarkdownLink,
} from "./markdown-link/scan";
import {
    PrettyMarkdownLinkWidget,
    PrettyMarkdownReferenceDefinitionWidget,
} from "./markdown-link/widgets";
import {
    collectMarkdownMathBlockRanges,
    findMarkdownInlineMathSyntaxMatches,
    rangeContainsMarkdownMath,
    rangeContainsMarkdownMathBlock,
} from "./markdown-math/scan";
import { PrettyMarkdownMathWidget } from "./markdown-math/widgets";
import {
    collectMarkdownRichBlockRanges,
    findMarkdownRichInlineMatches,
    rangeContainsMarkdownRichBlock,
    rangeContainsMarkdownRichInline,
} from "./markdown-rich/scan";
import { PrettyMarkdownInlineRichWidget } from "./markdown-rich/widgets";
import { MetaButtonGroupWidget, PrettyMetaWidget } from "./meta/widgets";
import {
    CONFIG_FENCE_RE,
    CONTENT_HEADER_RE,
    type ContentBlockInfo,
    DEFINE_CLOSE_RE,
    DEFINE_RE,
    FOR_CLOSE_RE,
    FOR_HEADER_RE,
    IMPORT_RE,
    type ImportLineInfo,
    INLINE_RENDER_RE,
    isClosingFence,
    isInsideCodeSpan,
    META_HEADER_RE,
    type MetaBlockInfo,
    parseAttrs,
    parseMarkdownFenceOpen,
    REF_RE,
    statusBgClass,
    USE_HEADER_RE,
    type UseBlockInfo,
} from "./patterns";
import {
    createRefResolveContext,
    getRefStatusMark,
    getRefStatusMessage,
    resolveRef,
} from "./ref/resolution";
import { PrettyRefWidget } from "./ref/widgets";
import { renderMark } from "./render/marks";
import {
    PrettyRenderBlockWidget,
    PrettyRenderInlineWidget,
} from "./render/widgets";
import {
    getCachedStatusBadgeWidget,
    getCachedStatusDotWidget,
    PrettyEndWidget,
} from "./shared/widgets";
import {
    cloudWorkerPluginNamesField,
    engineDefinitionsField,
    executionDurationField,
    executionErrorField,
    executionStatusField,
    forceL2Field,
    forExecutionDurationField,
    forExecutionStatusField,
    inlineRenderedDefinitionsField,
    parseDiagnosticsField,
    refResolveContextField,
    watchedUnitIdsField,
} from "./state-fields";
import type { DefineBlockInfo } from "./types";
import { PrettyUseWidget, UseButtonGroupWidget } from "./use/widgets";
import { useEditorStore } from "../../store/editor-store";

const CLOSE_LINE_RE = /^::\s*$/;
const BLOCK_OPENER_RE = /^::(define\[|content\[|meta\[|use\[|import\{)/;
const LOOP_VAR_REF_RE = /\$ref\{\$[^}]+\}/;
const MARKDOWN_HEADING_LINE_RE = /^ {0,3}#{1,6}(?:\s+|$)/;
const ACTION_WIDGET_SIDE = 10;
const STATUS_WIDGET_SIDE = ACTION_WIDGET_SIDE + 1;
const SYNTAX_FENCE_RE = /^(`{3,})(config|output|inputs)\s*$/;
type SyntaxFenceKind = "config" | "output" | "inputs";

export interface HtmlCommentBlockInfo {
    text: string;
    startLine: number;
    endLine: number;
}

function isMarkdownHeadingLine(text: string): boolean {
    return MARKDOWN_HEADING_LINE_RE.test(text);
}

function normalizeHtmlCommentText(lines: string[]): string {
    if (lines.length === 0) return "";
    const parts = [...lines];
    parts[0] = parts[0].replace(/^\s*<!--\s?/, "");
    parts[parts.length - 1] = parts[parts.length - 1].replace(/\s?-->\s*$/, "");
    return parts.map((part) => part.trim()).join("\n").trim();
}

function findAttachedCommentText(
    doc: EditorView["state"]["doc"],
    startLine: number,
    htmlCommentBlockByLine: Array<HtmlCommentBlockInfo | undefined>,
): string | null {
    let lineNumber = startLine - 1;
    while (lineNumber >= 1 && doc.line(lineNumber).text.trim() === "") {
        lineNumber--;
    }

    const comment = htmlCommentBlockByLine[lineNumber];
    return comment?.endLine === lineNumber ? comment.text : null;
}

/** Map for-execution status to CSS class(es) for line decoration. */
function getForLineClass(status: ForExecutionStatus): string {
    switch (status) {
        case "running":
            return "cm-magic-for-line cm-magic-for-line-running";
        case "success":
            return "cm-magic-for-line cm-magic-for-line-success";
        case "error":
            return "cm-magic-for-line cm-magic-for-line-error";
        default:
            return "cm-magic-for-line";
    }
}

function buildBlockLineIndex<T extends { startLine: number; endLine: number }>(
    blocks: readonly T[],
    totalLines: number,
): Array<T | undefined> {
    const index = new Array<T | undefined>(totalLines + 1);
    for (const block of blocks) {
        const blockSpan = block.endLine - block.startLine;
        for (let line = block.startLine; line <= block.endLine; line++) {
            const existing = index[line];
            if (!existing || blockSpan <= existing.endLine - existing.startLine) {
                index[line] = block;
            }
        }
    }
    return index;
}

function buildLineIndex<T extends { lineNumber: number }>(
    entries: readonly T[],
    totalLines: number,
): Array<T | undefined> {
    const index = new Array<T | undefined>(totalLines + 1);
    for (const entry of entries) {
        index[entry.lineNumber] = entry;
    }
    return index;
}

function hasFoldedRange(
    state: EditorView["state"],
    from: number,
    to: number,
): boolean {
    const folded = foldedRanges(state).iter();
    while (folded.value) {
        if (folded.from === from && folded.to === to) return true;
        folded.next();
    }
    return false;
}

function lineRangeUsesLoopVariableRef(
    doc: EditorView["state"]["doc"],
    startLine: number,
    endLine: number,
): boolean {
    let insideOutputFence = false;
    let outputFenceLength = 3;

    for (let lineNumber = startLine; lineNumber <= endLine; lineNumber++) {
        const text = doc.line(lineNumber).text;
        const trimmed = text.trim();
        const fenceMatch = trimmed.match(CONFIG_FENCE_RE);

        if (insideOutputFence) {
            if (isClosingFence(trimmed, outputFenceLength)) {
                insideOutputFence = false;
            }
            continue;
        }

        if (fenceMatch?.[2] === "output") {
            insideOutputFence = true;
            outputFenceLength = fenceMatch[1].length;
            continue;
        }

        if (LOOP_VAR_REF_RE.test(text)) return true;
    }

    return false;
}

function forEachRefInLineRange(
    doc: EditorView["state"]["doc"],
    startLine: number,
    endLine: number,
    visit: (refId: string) => void,
): void {
    let insideOutputFence = false;
    let outputFenceLength = 3;

    for (let lineNumber = startLine; lineNumber <= endLine; lineNumber++) {
        const text = doc.line(lineNumber).text;
        const trimmed = text.trim();
        const fenceMatch = trimmed.match(CONFIG_FENCE_RE);

        if (insideOutputFence) {
            if (isClosingFence(trimmed, outputFenceLength)) {
                insideOutputFence = false;
            }
            continue;
        }

        if (fenceMatch?.[2] === "output") {
            insideOutputFence = true;
            outputFenceLength = fenceMatch[1].length;
            continue;
        }

        for (const match of text.matchAll(REF_RE)) {
            const refId = match[1]?.trim();
            if (refId) visit(refId);
        }
    }
}

function isTemplateInputRef(refId: string): boolean {
    return (
        refId === "input" ||
        refId.startsWith("input/") ||
        refId.startsWith("input.")
    );
}

function extractLocalRefRoot(refId: string): string | null {
    if (refId.startsWith("$") || isTemplateInputRef(refId)) return null;
    const memberMatch = refId.match(/^(.+?)\.(?:output|body|content)(?:\.|$)/);
    return memberMatch?.[1] ?? refId;
}

function resolveLocalTemplateDependencyId(
    refId: string,
    localIds: ReadonlySet<string>,
): string | null {
    const root = extractLocalRefRoot(refId);
    if (!root) return null;
    if (localIds.has(root)) return root;

    const parts = root.split("/");
    for (let length = parts.length - 1; length >= 1; length--) {
        const candidate = parts.slice(0, length).join("/");
        if (localIds.has(candidate)) return candidate;
    }
    return null;
}

function collectForBlockRanges(
    doc: EditorView["state"]["doc"],
): Array<{ id: string; startLine: number; endLine: number }> {
    const ranges: Array<{ id: string; startLine: number; endLine: number }> = [];
    for (let lineNumber = 1; lineNumber <= doc.lines; lineNumber++) {
        const match = doc.line(lineNumber).text.trimStart().match(FOR_HEADER_RE);
        if (!match) continue;
        ranges.push({
            id: match[1],
            startLine: lineNumber,
            endLine: findForCloseLine(doc, lineNumber),
        });
    }
    return ranges;
}

function buildTemplateContentBlockedUnitIds(
    doc: EditorView["state"]["doc"],
    scan: Pick<MagicBlockScan, "contentBlocks" | "defineBlocks" | "useBlocks">,
): Set<string> {
    const blocked = new Set<string>();
    const forBlockRanges = collectForBlockRanges(doc);

    for (const templateBlock of scan.contentBlocks) {
        if (!templateBlock.isTemplate) continue;

        const unitRanges = [
            ...scan.defineBlocks.map((block) => ({
                id: block.defId,
                startLine: block.startLine,
                endLine: block.endLine,
            })),
            ...scan.useBlocks.map((block) => ({
                id: block.useId,
                startLine: block.startLine,
                endLine: block.endLine,
            })),
            ...scan.contentBlocks.map((block) => ({
                id: block.defId,
                startLine: block.startLine,
                endLine: block.endLine,
            })),
            ...forBlockRanges,
        ].filter(
            (range) =>
                range.startLine > templateBlock.startLine &&
                range.endLine < templateBlock.endLine,
        );
        if (unitRanges.length === 0) continue;

        const templateUnitIds = new Set(unitRanges.map((range) => range.id));
        const dependenciesById = new Map<string, Set<string>>();
        const directlyBlocked = new Set<string>();

        for (const range of unitRanges) {
            const dependencies = new Set<string>();
            forEachRefInLineRange(doc, range.startLine, range.endLine, (refId) => {
                if (isTemplateInputRef(refId)) {
                    directlyBlocked.add(range.id);
                    return;
                }
                const dependencyId = resolveLocalTemplateDependencyId(
                    refId,
                    templateUnitIds,
                );
                if (dependencyId && dependencyId !== range.id) {
                    dependencies.add(dependencyId);
                }
            });
            dependenciesById.set(range.id, dependencies);
        }

        for (const unitId of directlyBlocked) blocked.add(unitId);

        let changed = true;
        while (changed) {
            changed = false;
            for (const [unitId, dependencies] of dependenciesById) {
                if (blocked.has(unitId)) continue;
                for (const dependencyId of dependencies) {
                    if (!blocked.has(dependencyId)) continue;
                    blocked.add(unitId);
                    changed = true;
                    break;
                }
            }
        }
    }

    return blocked;
}

function findForCloseLine(
    doc: EditorView["state"]["doc"],
    startLine: number,
): number {
    let depth = 0;
    for (let lineNumber = startLine + 1; lineNumber <= doc.lines; lineNumber++) {
        const trimmedStart = doc.line(lineNumber).text.trimStart();
        if (FOR_HEADER_RE.test(trimmedStart)) {
            depth++;
            continue;
        }
        if (FOR_CLOSE_RE.test(trimmedStart.trim())) {
            if (depth > 0) {
                depth--;
            } else {
                return lineNumber;
            }
        }
    }
    return startLine;
}

const VIEWPORT_PREFETCH_LINES = 20;

function buildVisibleLineSpans(
    view: EditorView,
): Array<{ startLine: number; endLine: number }> {
    const spans: Array<{ startLine: number; endLine: number }> = [];
    const visibleRanges = Array.isArray(
        (view as { visibleRanges?: unknown }).visibleRanges,
    )
        ? view.visibleRanges
        : [{ from: 0, to: view.state.doc.length }];

    for (const range of visibleRanges) {
        const fromLine = Math.max(
            1,
            view.state.doc.lineAt(range.from).number - VIEWPORT_PREFETCH_LINES,
        );
        const toPos = Math.max(range.from, range.to - 1);
        const toLine = Math.min(
            view.state.doc.lines,
            view.state.doc.lineAt(toPos).number + VIEWPORT_PREFETCH_LINES,
        );

        const last = spans[spans.length - 1];
        if (last && fromLine <= last.endLine + 1) {
            last.endLine = Math.max(last.endLine, toLine);
        } else {
            spans.push({ startLine: fromLine, endLine: toLine });
        }
    }

    if (spans.length === 0) {
        spans.push({ startLine: 1, endLine: view.state.doc.lines });
    }

    return spans;
}

function isLineInVisibleSpans(
    lineNumber: number,
    spans: Array<{ startLine: number; endLine: number }>,
): boolean {
    for (const span of spans) {
        if (lineNumber < span.startLine) return false;
        if (lineNumber <= span.endLine) return true;
    }
    return false;
}

function rangeIntersectsVisibleSpans(
    startLine: number,
    endLine: number,
    spans: Array<{ startLine: number; endLine: number }>,
): boolean {
    for (const span of spans) {
        if (endLine < span.startLine) return false;
        if (startLine <= span.endLine) return true;
    }
    return false;
}

function getIndentedSyntaxFrom(line: { from: number; text: string }): number {
    return line.from + (line.text.match(/^\s*/)?.[0].length ?? 0);
}

function getFenceLineClass(info: SyntaxFenceLineInfo): string {
    return [
        "cm-magic-fence-line",
        `cm-magic-fence-line-${info.kind}`,
        `cm-magic-fence-line-${info.role}`,
    ].join(" ");
}

function getFenceMarkClass(info: SyntaxFenceLineInfo): string {
    return [
        "cm-magic-fence-mark",
        `cm-magic-fence-mark-${info.kind}`,
        `cm-magic-fence-mark-${info.role}`,
    ].join(" ");
}

function rangeHasCursor(
    startLine: number,
    endLine: number,
    activeCursorLines: Set<number>,
): boolean {
    for (let i = startLine; i <= endLine; i++) {
        if (activeCursorLines.has(i)) return true;
    }
    return false;
}

function selectionTouchesRange(
    view: EditorView,
    from: number,
    to: number,
): boolean {
    for (const range of view.state.selection.ranges) {
        if (range.empty) {
            if (range.head >= from && range.head <= to) return true;
            continue;
        }
        if (range.from < to && range.to > from) return true;
    }
    return false;
}

export interface MagicBlockScan {
    defineBlocks: DefineBlockInfo[];
    defineBlockByLine: Array<DefineBlockInfo | undefined>;
    htmlCommentBlocks: HtmlCommentBlockInfo[];
    htmlCommentBlockByLine: Array<HtmlCommentBlockInfo | undefined>;
    syntaxCommentByTargetId: Map<string, string>;
    metaBlocks: MetaBlockInfo[];
    metaBlockByLine: Array<MetaBlockInfo | undefined>;
    importLines: ImportLineInfo[];
    importByLine: Array<ImportLineInfo | undefined>;
    importBlockByLine: Array<ImportLineInfo | undefined>;
    useBlocks: UseBlockInfo[];
    useBlockByLine: Array<UseBlockInfo | undefined>;
    contentBlocks: ContentBlockInfo[];
    contentBlockByLine: Array<ContentBlockInfo | undefined>;
    localDefIds: Set<string>;
    localContentIds: Set<string>;
    localForIds: Set<string>;
    localUseIds: Set<string>;
    localDefPluginMap: Map<string, string>;
    outputFenceLines: Set<number>;
    syntaxFenceByLine: Array<SyntaxFenceLineInfo | undefined>;
    fenceBodyRanges: FenceBodyRange[];
    /**
     * 行号集合：处于顶层标准 Markdown 围栏代码块（``` / ~~~）内的行（包含开/闭围栏行本身）。
     * 这些行内的 $ref{} / ::render[] 等 Magic MD 语法应被视为字面文本，不做识别或装饰。
     */
    mdFenceLines: Set<number>;
    /**
     * 文档是否包含 ::meta[snapshot] 块。
     */
    hasMetaSnapshot: boolean;
}

export interface SyntaxFenceLineInfo {
    kind: SyntaxFenceKind;
    role: "open" | "body" | "close";
    length: number;
}

/** A contiguous syntax fence body ready for JSON-tree rendering. */
export interface FenceBodyRange {
    kind: SyntaxFenceKind;
    /** 1-based line number of the first body line. */
    startLine: number;
    /** 1-based line number of the last body line (inclusive). */
    endLine: number;
}

export function collectMarkdownMathExcludedLines(
    scan: Pick<
        MagicBlockScan,
        "mdFenceLines" | "outputFenceLines" | "syntaxFenceByLine" | "fenceBodyRanges"
    >,
): Set<number> {
    const excludedLines = new Set<number>(scan.mdFenceLines);
    for (const lineNumber of scan.outputFenceLines) {
        excludedLines.add(lineNumber);
    }
    for (let lineNumber = 1; lineNumber < scan.syntaxFenceByLine.length; lineNumber += 1) {
        if (scan.syntaxFenceByLine[lineNumber]) {
            excludedLines.add(lineNumber);
        }
    }
    for (const range of scan.fenceBodyRanges) {
        for (let lineNumber = range.startLine; lineNumber <= range.endLine; lineNumber += 1) {
            excludedLines.add(lineNumber);
        }
    }
    return excludedLines;
}

function buildMetaSummary(domain: string, configLines: string[]): string {
    const configText = configLines.join("\n").trim();
    if (!configText) return "";

    try {
        const parsed = JSON.parse(configText) as Record<string, unknown>;
        if (domain === "config" && (parsed.mode === "external" || parsed.mode === "inline")) {
            return tr("syntax.summary.configStorageMode", { mode: parsed.mode });
        }
        if (Array.isArray(parsed.exports)) {
            return tr("syntax.summary.exports", { count: parsed.exports.length });
        }

        if (domain === "publish") {
            const target =
                typeof parsed.target === "string"
                    ? parsed.target
                    : typeof parsed.platform === "string"
                        ? parsed.platform
                        : null;
            if (target) return tr("syntax.summary.publishTarget", { target });
        }

        if (domain === "module") {
            const keyCount = Object.keys(parsed).length;
            return tr("syntax.summary.moduleConfigItems", { count: keyCount });
        }

        const keyCount = Object.keys(parsed).length;
        return tr("syntax.summary.domainConfigItems", { domain, count: keyCount });
    } catch {
        return tr("syntax.summary.jsonError");
    }
}

function buildUseSummary(configLines: string[]): string {
    const configText = configLines.join("\n").trim();
    if (!configText) return "";

    try {
        const parsed = JSON.parse(configText) as Record<string, unknown>;
        const parts: string[] = [];
        if (parsed.patch && typeof parsed.patch === "object") {
            parts.push(tr("syntax.summary.patchItems", { count: Object.keys(parsed.patch as Record<string, unknown>).length }));
        }
        if (parsed.bind && typeof parsed.bind === "object") {
            parts.push(tr("syntax.summary.bindItems", { count: Object.keys(parsed.bind as Record<string, unknown>).length }));
        }
        if (parsed.with && typeof parsed.with === "object") {
            parts.push(tr("syntax.summary.withItems", { count: Object.keys(parsed.with as Record<string, unknown>).length }));
        }
        return parts.join(tr("common.listSeparator"));
    } catch {
        return tr("syntax.summary.jsonError");
    }
}

function truncateSummary(value: string, max = 32): string {
    return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function buildImportSummary(importInfo: ImportLineInfo): string {
    const parts: string[] = [];
    if (importInfo.pick.length > 0) {
        parts.push(tr("syntax.summary.importPicked", { count: importInfo.pick.length }));
    } else if (importInfo.exclude.length > 0) {
        parts.push(tr("syntax.summary.importAllExcept", { count: importInfo.exclude.length }));
    } else {
        parts.push(tr("syntax.summary.importAll"));
    }
    const renameCount = Object.keys(importInfo.rename).length;
    if (renameCount > 0) parts.push(tr("syntax.summary.renameItems", { count: renameCount }));
    return parts.join(tr("common.listSeparator"));
}

function buildDefineSummary(
    pluginName: string,
    configLines: string[],
    pluginMeta?: { title?: string; overview?: string; description?: string },
): string {
    const configText = configLines.join("\n").trim();
    if (!configText) {
        if (pluginMeta?.title) return truncateSummary(pluginMeta.title);
        if (pluginMeta?.description) return truncateSummary(pluginMeta.description);
        return tr("syntax.summary.runPlugin", { pluginName });
    }

    try {
        const parsed = JSON.parse(configText) as Record<string, unknown>;
        if (typeof parsed.url === "string") {
            return tr("syntax.summary.request", { value: truncateSummary(parsed.url.replace(/^https?:\/\//, "")) });
        }
        if (typeof parsed.query === "string") {
            return tr("syntax.summary.query", { value: truncateSummary(parsed.query.replace(/\s+/g, " ")) });
        }
        if (typeof parsed.sql === "string") {
            return `SQL ${truncateSummary(parsed.sql.replace(/\s+/g, " "))}`;
        }
        if (typeof parsed.path === "string") {
            return tr("syntax.summary.read", { value: truncateSummary(parsed.path) });
        }
        if (typeof parsed.filePath === "string") {
            return tr("syntax.summary.read", { value: truncateSummary(parsed.filePath) });
        }
        if (typeof parsed.source === "string") {
            return tr("syntax.summary.use", { value: truncateSummary(parsed.source) });
        }
        if (pluginMeta?.title) return truncateSummary(pluginMeta.title);
        return tr("syntax.summary.configItems", { count: Object.keys(parsed).length });
    } catch {
        if (pluginMeta?.title) return truncateSummary(pluginMeta.title);
        return tr("syntax.summary.runPlugin", { pluginName });
    }
}

function buildContentSummary(
    isTemplate: boolean,
    bodyLineCount: number,
    refCount: number,
    contentExecution?: { status?: string; meta?: { total: number; succeeded: number; failed: number } },
): string {
    if (contentExecution?.status === "running") {
        return isTemplate ? tr("syntax.summary.templateRunning") : tr("syntax.summary.contentRunning");
    }
    if (contentExecution?.meta) {
        if (contentExecution.meta.failed > 0) {
            return tr("syntax.summary.generatedPartial", { succeeded: contentExecution.meta.succeeded, total: contentExecution.meta.total });
        }
        return tr("syntax.summary.generated", { count: contentExecution.meta.total });
    }
    const parts = [isTemplate ? tr("syntax.summary.template") : tr("syntax.summary.contentBlock")];
    if (bodyLineCount > 0) parts.push(tr("syntax.summary.lines", { count: bodyLineCount }));
    if (refCount > 0) parts.push(tr("syntax.summary.refs", { count: refCount }));
    return parts.join(tr("common.listSeparator"));
}

function buildContentDisplaySummary(
    staticSummary: string,
    contentExecution?: { status?: string; meta?: { total: number; succeeded: number; failed: number } },
): string {
    if (contentExecution?.status === "running") {
        return staticSummary
            ? tr("syntax.summary.runningWithSummary", { summary: staticSummary })
            : tr("syntax.summary.contentRunning");
    }
    if (contentExecution?.meta) {
        if (contentExecution.meta.failed > 0) {
            return tr("syntax.summary.generatedPartial", { succeeded: contentExecution.meta.succeeded, total: contentExecution.meta.total });
        }
        return tr("syntax.summary.generated", { count: contentExecution.meta.total });
    }
    return staticSummary;
}

function buildForSummary(
    attrsStr: string,
    ofExpr: string,
    forExecution?: {
        status?: string;
        totalIterations?: number;
        completedIterations?: number;
        meta?: { total: number; succeeded: number; failed: number };
    },
): string {
    if (forExecution?.status === "running") {
        const total = Math.max(
            forExecution.totalIterations ?? 0,
            forExecution.meta?.total ?? 0,
        );
        return tr("syntax.summary.inProgress", { current: forExecution.completedIterations ?? 0, total });
    }
    if (forExecution?.meta) {
        if (forExecution.meta.failed > 0) {
            return tr("syntax.summary.completedPartial", { succeeded: forExecution.meta.succeeded, total: forExecution.meta.total });
        }
        return tr("syntax.summary.iterationsCompleted", { count: forExecution.meta.total });
    }
    const limitMatch = attrsStr.match(/limit="([^"]*)"/);
    const concurrencyMatch = attrsStr.match(/concurrency="([^"]*)"/);
    const parts = [tr("syntax.summary.iterate", { value: truncateSummary(ofExpr) })];
    if (limitMatch?.[1]) parts.push(`limit ${limitMatch[1]}`);
    if (concurrencyMatch?.[1] && concurrencyMatch[1] !== "1") {
        parts.push(tr("syntax.summary.concurrency", { count: concurrencyMatch[1] }));
    }
    return parts.join(tr("common.listSeparator"));
}

function buildImportDisplaySummary(
    importInfo: ImportLineInfo,
    importExecution?: { status?: string; meta?: { total: number } },
): string {
    if (importExecution?.status === "running") return tr("syntax.summary.importResolving");
    if (importExecution?.meta?.total) return tr("syntax.summary.importResolved", { count: importExecution.meta.total });
    return buildImportSummary(importInfo);
}

function buildUseDisplaySummary(
    staticSummary: string,
    useExecution?: { status?: string; materializedId?: string; sourceName?: string },
): string {
    if (useExecution?.status === "running") return tr("syntax.summary.instantiating");
    if (useExecution?.materializedId && useExecution?.sourceName) {
        return tr("syntax.summary.instantiatedFrom", { source: useExecution.sourceName });
    }
    return staticSummary;
}

function buildDefineDisplaySummary(
    staticSummary: string,
    pluginName: string,
    pluginMeta?: { title?: string; overview?: string; description?: string },
): string {
    const isWeakStaticSummary =
        !staticSummary ||
        staticSummary === tr("syntax.summary.runPlugin", { pluginName }) ||
        new RegExp(`^${tr("syntax.summary.configItems", { count: "\\d+" })}$`).test(staticSummary);

    if (!isWeakStaticSummary) {
        return staticSummary;
    }
    if (pluginMeta?.title) return truncateSummary(pluginMeta.title);
    if (pluginMeta?.description) return truncateSummary(pluginMeta.description);
    return staticSummary || tr("syntax.summary.runPlugin", { pluginName });
}

function getRenderDisplayInfo(
    renderId: string,
    localDefIds: Set<string>,
    localContentIds: Set<string>,
    defPluginMap: Map<string, string>,
    syntaxCommentByTargetId: Map<string, string>,
): {
    targetKind: string;
    summary: string;
    pluginName: string | null;
    comment: string | null;
} {
    const comment = syntaxCommentByTargetId.get(renderId) ?? null;
    if (localContentIds.has(renderId)) {
        return {
            targetKind: "content",
            summary: "",
            pluginName: null,
            comment,
        };
    }

    const pluginName = defPluginMap.get(renderId);
    if (!localDefIds.has(renderId)) {
        return {
            targetKind: "module",
            summary: pluginName ?? "",
            pluginName: pluginName ?? null,
            comment,
        };
    }

    return {
        targetKind: "definition",
        summary: pluginName ?? tr("syntax.summary.executionResult"),
        pluginName: pluginName ?? null,
        comment,
    };
}
function findImportBlockRange(
    doc: EditorView["state"]["doc"],
    lineNumber: number,
): { endLine: number; outputRange?: { startLine: number; endLine: number } } {
    let cursor = lineNumber + 1;
    while (cursor <= doc.lines && doc.line(cursor).text.trim() === "") cursor++;

    if (cursor > doc.lines) return { endLine: lineNumber };

    const fenceMatch = CONFIG_FENCE_RE.exec(doc.line(cursor).text.trim());
    if (!fenceMatch || fenceMatch[2] !== "output") return { endLine: lineNumber };

    const fenceLen = fenceMatch[1].length;
    let fenceCloseLine = cursor + 1;
    while (fenceCloseLine <= doc.lines) {
        const trimmed = doc.line(fenceCloseLine).text.trim();
        if (isClosingFence(trimmed, fenceLen)) break;
        fenceCloseLine++;
    }

    if (fenceCloseLine > doc.lines) return { endLine: lineNumber };

    let closeLine = fenceCloseLine + 1;
    while (closeLine <= doc.lines && doc.line(closeLine).text.trim() === "") closeLine++;

    return {
        endLine:
            closeLine <= doc.lines && CLOSE_LINE_RE.test(doc.line(closeLine).text.trim())
                ? closeLine
                : fenceCloseLine,
        outputRange: { startLine: cursor, endLine: fenceCloseLine },
    };
}

function parseImportLine(
    attrsStr: string,
    lineNumber: number,
    blockRange: { endLine: number; outputRange?: { startLine: number; endLine: number } },
): ImportLineInfo {
    const pickMatch = attrsStr.match(/pick="([^"]*)"/);
    const fromMatch = attrsStr.match(/from="([^"]*)"/);
    const nsMatch = attrsStr.match(/ns="([^"]*)"/);
    const excludeMatch = attrsStr.match(/exclude="([^"]*)"/);
    const renameMatch = attrsStr.match(/rename="([^"]*)"/);
    const rename: Record<string, string> = {};

    if (renameMatch?.[1]) {
        for (const pair of renameMatch[1].split(",")) {
            const [from, to] = pair.split(":").map((part) => part.trim());
            if (from && to) rename[from] = to;
        }
    }

    return {
        namespace: nsMatch?.[1] ?? "",
        source: fromMatch?.[1] ?? "",
        lineNumber,
        startLine: lineNumber,
        endLine: blockRange.endLine,
        outputRange: blockRange.outputRange,
        pick: pickMatch?.[1]
            ? pickMatch[1]
                .split(",")
                .map((part) => part.trim())
                .filter(Boolean)
            : [],
        exclude: excludeMatch?.[1]
            ? excludeMatch[1]
                .split(",")
                .map((part) => part.trim())
                .filter(Boolean)
            : [],
        rename,
    };
}

export interface ScanMagicBlocksOptions {
    ignoreContentCloseLines?: ReadonlySet<number>;
}

export function scanMagicBlocks(
    doc: EditorView["state"]["doc"],
    options: ScanMagicBlocksOptions = {},
): MagicBlockScan {
    const defineBlocks: DefineBlockInfo[] = [];
    const htmlCommentBlocks: HtmlCommentBlockInfo[] = [];
    const metaBlocks: MetaBlockInfo[] = [];
    const importLines: ImportLineInfo[] = [];
    const useBlocks: UseBlockInfo[] = [];
    const contentBlocks: ContentBlockInfo[] = [];
    const outputFenceLines = new Set<number>();
    const syntaxFenceLines = new Map<number, SyntaxFenceLineInfo>();

    let currentDefine: {
        defId: string;
        pluginName: string;
        isTemplate: boolean;
        startLine: number;
        configLines: string[];
        activeFenceKind: SyntaxFenceKind | "";
        activeFenceLength: number;
    } | null = null;
    let currentMeta: {
        domain: string;
        startLine: number;
        configLines: string[];
        inFence: boolean;
    } | null = null;
    let currentUse: {
        useId: string;
        source: string;
        isTemplate: boolean;
        startLine: number;
        configLines: string[];
        activeFenceKind: SyntaxFenceKind | "";
        activeFenceLength: number;
    } | null = null;
    type ContentScanFrame = {
        defId: string;
        isTemplate: boolean;
        startLine: number;
        nestingDepth: number;
        forNestingDepth: number;
        bodyLineCount: number;
        refCount: number;
    };
    const contentStack: ContentScanFrame[] = [];
    const getCurrentContent = (): ContentScanFrame | null =>
        contentStack[contentStack.length - 1] ?? null;
    const pushContentBlock = (content: ContentScanFrame, endLine: number): void => {
        contentBlocks.push({
            defId: content.defId,
            isTemplate: content.isTemplate,
            summary: buildContentSummary(
                content.isTemplate,
                content.bodyLineCount,
                content.refCount,
            ),
            startLine: content.startLine,
            endLine,
        });
    };
    let currentHtmlComment: {
        startLine: number;
        lines: string[];
    } | null = null;
    let outputFenceActive = false;
    let outputFenceLength = 3;
    let outputBlockDepth = 0;
    let inImportOutputBlock = false;
    let contentFence: { kind: SyntaxFenceKind; length: number } | null = null;
    let standaloneSyntaxFence: { kind: SyntaxFenceKind; length: number } | null = null;
    // 标准 Markdown 围栏代码块（``` / ~~~）— 仅在顶层作用域生效，
    // 围栏内的所有文本保持原样，不识别 Magic MD 指令。
    let mdFence: { char: "`" | "~"; length: number } | null = null;
    const mdFenceLines = new Set<number>();
    const ignoreContentCloseLines = options.ignoreContentCloseLines ?? new Set<number>();

    for (let i = 1; i <= doc.lines; i++) {
        const text = doc.line(i).text;

        // ── Markdown fenced code block 处理（顶层作用域） ──
        if (mdFence) {
            mdFenceLines.add(i);
            const trimmedLine = text.trim();
            if (
                trimmedLine.length >= mdFence.length &&
                trimmedLine === mdFence.char.repeat(trimmedLine.length) &&
                /^[`~]+$/.test(trimmedLine)
            ) {
                mdFence = null;
            }
            continue;
        }
        // 仅当处于顶层（无 directive 状态）时才考虑进入 markdown fence
        if (
            !currentDefine &&
            !currentMeta &&
            !currentUse &&
            contentStack.length === 0 &&
            outputBlockDepth === 0 &&
            !inImportOutputBlock &&
            !outputFenceActive &&
            !standaloneSyntaxFence
        ) {
            const fenceOpen = parseMarkdownFenceOpen(text.trim());
            // ```config / ```output / ```inputs 由 directive scanner 拥有，不视为不透明围栏
            if (
                fenceOpen &&
                fenceOpen.info !== "config" &&
                fenceOpen.info !== "output" &&
                fenceOpen.info !== "inputs"
            ) {
                mdFence = { char: fenceOpen.char, length: fenceOpen.length };
                mdFenceLines.add(i);
                continue;
            }
        }

        // ── Fast prefix check ──
        // Magic MD directive lines start with `:` or `` ` `` (after optional
        // whitespace).  If the first non-space character is anything else, the
        // only possible work is collecting config lines inside an open fence.
        // This avoids .trim() + multiple regex tests on long data lines (e.g.
        // 3.8 MB JSON inside ```output fences).
        const canScanHtmlComment =
            !outputFenceActive &&
            !inImportOutputBlock &&
            !contentFence &&
            !standaloneSyntaxFence &&
            !currentDefine?.activeFenceKind &&
            !currentMeta?.inFence &&
            !currentUse?.activeFenceKind;
        const trimmedForComment = text.trim();
        if (currentHtmlComment && canScanHtmlComment) {
            if (
                getCurrentContent() &&
                getCurrentContent()?.nestingDepth === 0 &&
                trimmedForComment.length > 0
            ) {
                const currentContent = getCurrentContent();
                if (currentContent) currentContent.bodyLineCount++;
            }
            currentHtmlComment.lines.push(text);
            if (trimmedForComment.endsWith("-->")) {
                htmlCommentBlocks.push({
                    text: normalizeHtmlCommentText(currentHtmlComment.lines),
                    startLine: currentHtmlComment.startLine,
                    endLine: i,
                });
                currentHtmlComment = null;
            }
            continue;
        }
        if (
            canScanHtmlComment &&
            trimmedForComment.startsWith("<!--") &&
            (trimmedForComment.endsWith("-->") || !trimmedForComment.includes("-->"))
        ) {
            if (
                getCurrentContent() &&
                getCurrentContent()?.nestingDepth === 0 &&
                trimmedForComment.length > 0
            ) {
                const currentContent = getCurrentContent();
                if (currentContent) currentContent.bodyLineCount++;
            }
            if (trimmedForComment.endsWith("-->")) {
                htmlCommentBlocks.push({
                    text: normalizeHtmlCommentText([text]),
                    startLine: i,
                    endLine: i,
                });
            } else {
                currentHtmlComment = { startLine: i, lines: [text] };
            }
            continue;
        }

        let fc = 0;
        for (let j = 0; j < text.length; j++) {
            const c = text.charCodeAt(j);
            if (c !== 32 /* ' ' */ && c !== 9 /* '\t' */) { fc = c; break; }
        }
        const canBeDirective = fc === 58 /* ':' */ || fc === 96 /* '`' */;

        if (!canBeDirective) {
            if (standaloneSyntaxFence) {
                const trimmedLine = text.trim();
                if (isClosingFence(trimmedLine, standaloneSyntaxFence.length)) {
                    syntaxFenceLines.set(i, {
                        kind: standaloneSyntaxFence.kind,
                        role: "close",
                        length: standaloneSyntaxFence.length,
                    });
                    standaloneSyntaxFence = null;
                } else {
                    syntaxFenceLines.set(i, {
                        kind: standaloneSyntaxFence.kind,
                        role: "body",
                        length: standaloneSyntaxFence.length,
                    });
                }
                continue;
            }

            if (outputFenceActive) {
                const trimmedLine = text.trim();
                outputFenceLines.add(i);
                if (
                    trimmedLine.length === outputFenceLength &&
                    trimmedLine === "`".repeat(outputFenceLength)
                ) {
                    syntaxFenceLines.set(i, {
                        kind: "output",
                        role: "close",
                        length: outputFenceLength,
                    });
                    outputFenceActive = false;
                } else {
                    syntaxFenceLines.set(i, {
                        kind: "output",
                        role: "body",
                        length: outputFenceLength,
                    });
                }
            }

            // Not a directive — but if we are inside a config fence, collect content
            if (currentDefine?.activeFenceKind) {
                syntaxFenceLines.set(i, {
                    kind: currentDefine.activeFenceKind,
                    role: "body",
                    length: currentDefine.activeFenceLength,
                });
                if (currentDefine.activeFenceKind === "config") {
                    currentDefine.configLines.push(text);
                }
            }
            if (currentMeta?.inFence) {
                currentMeta.configLines.push(text);
            }
            if (currentUse?.activeFenceKind) {
                syntaxFenceLines.set(i, {
                    kind: currentUse.activeFenceKind,
                    role: "body",
                    length: currentUse.activeFenceLength,
                });
                if (currentUse.activeFenceKind === "config") {
                    currentUse.configLines.push(text);
                }
            }
            if (contentFence) {
                mdFenceLines.add(i);
                const trimmedLine = text.trim();
                if (isClosingFence(trimmedLine, contentFence.length)) {
                    syntaxFenceLines.set(i, {
                        kind: contentFence.kind,
                        role: "close",
                        length: contentFence.length,
                    });
                    contentFence = null;
                } else {
                    syntaxFenceLines.set(i, {
                        kind: contentFence.kind,
                        role: "body",
                        length: contentFence.length,
                    });
                }
                continue;
            }
            const currentContent = getCurrentContent();
            if (currentContent && currentContent.nestingDepth === 0) {
                const trimmedLine = text.trim();
                if (trimmedLine.length > 0) {
                    currentContent.bodyLineCount++;
                }
                const refMatches = text.match(REF_RE);
                if (refMatches) {
                    currentContent.refCount += refMatches.length;
                }
            }
            continue;
        }

        const trimmed = text.trim();
        const trimmedStart = text.trimStart();

        if (standaloneSyntaxFence) {
            if (isClosingFence(trimmed, standaloneSyntaxFence.length)) {
                syntaxFenceLines.set(i, {
                    kind: standaloneSyntaxFence.kind,
                    role: "close",
                    length: standaloneSyntaxFence.length,
                });
                standaloneSyntaxFence = null;
            } else {
                syntaxFenceLines.set(i, {
                    kind: standaloneSyntaxFence.kind,
                    role: "body",
                    length: standaloneSyntaxFence.length,
                });
            }
            continue;
        }

        if (contentFence) {
            mdFenceLines.add(i);
            if (isClosingFence(trimmed, contentFence.length)) {
                syntaxFenceLines.set(i, {
                    kind: contentFence.kind,
                    role: "close",
                    length: contentFence.length,
                });
                contentFence = null;
            } else {
                syntaxFenceLines.set(i, {
                    kind: contentFence.kind,
                    role: "body",
                    length: contentFence.length,
                });
            }
            continue;
        }

        if (outputBlockDepth === 0 && !inImportOutputBlock && IMPORT_RE.test(trimmed)) {
            let peek = i + 1;
            while (peek <= doc.lines && doc.line(peek).text.trim() === "") peek++;
            if (peek <= doc.lines) {
                const peekTrimmed = doc.line(peek).text.trim();
                if (peekTrimmed.match(/^`{3,}output/)) {
                    inImportOutputBlock = true;
                }
            }
        } else if (inImportOutputBlock) {
            if (outputFenceActive) {
                outputFenceLines.add(i);
                if (
                    trimmed.length === outputFenceLength &&
                    trimmed === "`".repeat(outputFenceLength)
                ) {
                    syntaxFenceLines.set(i, {
                        kind: "output",
                        role: "close",
                        length: outputFenceLength,
                    });
                    outputFenceActive = false;
                } else {
                    syntaxFenceLines.set(i, {
                        kind: "output",
                        role: "body",
                        length: outputFenceLength,
                    });
                }
            } else if (trimmed.startsWith("```")) {
                const outputFenceMatch = trimmed.match(/^(`{3,})output/);
                if (outputFenceMatch) {
                    outputFenceActive = true;
                    outputFenceLength = outputFenceMatch[1].length;
                    outputFenceLines.add(i);
                    syntaxFenceLines.set(i, {
                        kind: "output",
                        role: "open",
                        length: outputFenceLength,
                    });
                }
            } else if (DEFINE_CLOSE_RE.test(trimmed)) {
                inImportOutputBlock = false;
            } else if (trimmed !== "") {
                inImportOutputBlock = false;
            }

            if (inImportOutputBlock) {
                continue;
            }
        }

        if (
            DEFINE_RE.test(trimmedStart) ||
            USE_HEADER_RE.test(trimmed) ||
            CONTENT_HEADER_RE.test(trimmed) ||
            FOR_HEADER_RE.test(trimmed)
        ) {
            outputBlockDepth++;
        } else if (
            outputBlockDepth > 0 &&
            (DEFINE_CLOSE_RE.test(trimmed) || FOR_CLOSE_RE.test(trimmed))
        ) {
            outputFenceActive = false;
            outputBlockDepth--;
        }

        if (outputBlockDepth > 0) {
            if (outputFenceActive) {
                outputFenceLines.add(i);
                if (
                    trimmed.length === outputFenceLength &&
                    trimmed === "`".repeat(outputFenceLength)
                ) {
                    syntaxFenceLines.set(i, {
                        kind: "output",
                        role: "close",
                        length: outputFenceLength,
                    });
                    outputFenceActive = false;
                } else {
                    syntaxFenceLines.set(i, {
                        kind: "output",
                        role: "body",
                        length: outputFenceLength,
                    });
                }
            } else if (trimmed.startsWith("```")) {
                const outputFenceMatch = trimmed.match(/^(`{3,})output/);
                if (outputFenceMatch) {
                    outputFenceActive = true;
                    outputFenceLength = outputFenceMatch[1].length;
                    outputFenceLines.add(i);
                    syntaxFenceLines.set(i, {
                        kind: "output",
                        role: "open",
                        length: outputFenceLength,
                    });
                }
            }
        }

        if (
            outputBlockDepth > 0 &&
            !currentDefine &&
            !currentMeta &&
            !currentUse &&
            contentStack.length === 0
        ) {
            const fenceMatch = SYNTAX_FENCE_RE.exec(trimmed);
            if (fenceMatch?.[2] === "inputs") {
                standaloneSyntaxFence = {
                    kind: "inputs",
                    length: fenceMatch[1].length,
                };
                syntaxFenceLines.set(i, {
                    kind: "inputs",
                    role: "open",
                    length: standaloneSyntaxFence.length,
                });
                continue;
            }
        }

        const defineMatch = trimmedStart.match(DEFINE_RE);
        if (defineMatch) {
            const attrs = parseAttrs(defineMatch[2]);
            currentDefine = {
                defId: defineMatch[1],
                pluginName: attrs.plugin ?? "",
                isTemplate: attrs.template === "true",
                startLine: i,
                configLines: [],
                activeFenceKind: "",
                activeFenceLength: 0,
            };
        } else if (currentDefine?.activeFenceKind) {
            if (isClosingFence(trimmed, currentDefine.activeFenceLength)) {
                syntaxFenceLines.set(i, {
                    kind: currentDefine.activeFenceKind,
                    role: "close",
                    length: currentDefine.activeFenceLength,
                });
                currentDefine.activeFenceKind = "";
                currentDefine.activeFenceLength = 0;
            } else {
                syntaxFenceLines.set(i, {
                    kind: currentDefine.activeFenceKind,
                    role: "body",
                    length: currentDefine.activeFenceLength,
                });
                if (currentDefine.activeFenceKind === "config") {
                    currentDefine.configLines.push(text);
                }
            }
        } else if (currentDefine) {
            const fenceMatch = SYNTAX_FENCE_RE.exec(trimmed);
            if (fenceMatch) {
                currentDefine.activeFenceKind = fenceMatch[2] as SyntaxFenceKind;
                currentDefine.activeFenceLength = fenceMatch[1].length;
                syntaxFenceLines.set(i, {
                    kind: currentDefine.activeFenceKind,
                    role: "open",
                    length: currentDefine.activeFenceLength,
                });
            } else if (DEFINE_CLOSE_RE.test(trimmed)) {
                defineBlocks.push({
                    defId: currentDefine.defId,
                    pluginName: currentDefine.pluginName,
                    isTemplate: currentDefine.isTemplate,
                    summary: buildDefineSummary(
                        currentDefine.pluginName,
                        currentDefine.configLines,
                    ),
                    startLine: currentDefine.startLine,
                    endLine: i,
                });
                currentDefine = null;
            }
        }

        if (!currentMeta) {
            const metaMatch = trimmed.match(META_HEADER_RE);
            if (metaMatch) {
                currentMeta = {
                    domain: metaMatch[1],
                    startLine: i,
                    configLines: [],
                    inFence: false,
                };
            }
        } else if (trimmed.startsWith("```") && !currentMeta.inFence) {
            currentMeta.inFence = true;
            const fenceMatch = SYNTAX_FENCE_RE.exec(trimmed);
            if (fenceMatch) {
                syntaxFenceLines.set(i, {
                    kind: fenceMatch[2] as SyntaxFenceKind,
                    role: "open",
                    length: fenceMatch[1].length,
                });
            }
        } else if (trimmed.startsWith("```") && currentMeta.inFence) {
            syntaxFenceLines.set(i, {
                kind: "config",
                role: "close",
                length: trimmed.match(/^`+/)?.[0].length ?? 3,
            });
            currentMeta.inFence = false;
        } else if (currentMeta.inFence) {
            currentMeta.configLines.push(text);
        } else if (CLOSE_LINE_RE.test(trimmed)) {
            metaBlocks.push({
                domain: currentMeta.domain,
                startLine: currentMeta.startLine,
                endLine: i,
                summary: buildMetaSummary(currentMeta.domain, currentMeta.configLines),
            });
            currentMeta = null;
        }

        const importMatch = trimmed.match(IMPORT_RE);
        if (importMatch) {
            importLines.push(parseImportLine(importMatch[1], i, findImportBlockRange(doc, i)));
        }

        if (!currentUse) {
            const useMatch = trimmed.match(USE_HEADER_RE);
            if (useMatch) {
                const fromMatch = useMatch[2].match(/from="([^"]*)"/);
                currentUse = {
                    useId: useMatch[1],
                    source: fromMatch?.[1] ?? "",
                    isTemplate: /template="true"/.test(useMatch[2]),
                    startLine: i,
                    configLines: [],
                    activeFenceKind: "",
                    activeFenceLength: 0,
                };
            }
        } else if (currentUse.activeFenceKind) {
            if (isClosingFence(trimmed, currentUse.activeFenceLength)) {
                syntaxFenceLines.set(i, {
                    kind: currentUse.activeFenceKind,
                    role: "close",
                    length: currentUse.activeFenceLength,
                });
                currentUse.activeFenceKind = "";
                currentUse.activeFenceLength = 0;
            } else {
                syntaxFenceLines.set(i, {
                    kind: currentUse.activeFenceKind,
                    role: "body",
                    length: currentUse.activeFenceLength,
                });
                if (currentUse.activeFenceKind === "config") {
                    currentUse.configLines.push(text);
                }
            }
        } else {
            const fenceMatch = SYNTAX_FENCE_RE.exec(trimmed);
            if (fenceMatch) {
                currentUse.activeFenceKind = fenceMatch[2] as SyntaxFenceKind;
                currentUse.activeFenceLength = fenceMatch[1].length;
                syntaxFenceLines.set(i, {
                    kind: currentUse.activeFenceKind,
                    role: "open",
                    length: currentUse.activeFenceLength,
                });
            } else if (CLOSE_LINE_RE.test(trimmed)) {
                useBlocks.push({
                    useId: currentUse.useId,
                    source: currentUse.source,
                    isTemplate: currentUse.isTemplate,
                    modifiers: buildUseSummary(currentUse.configLines),
                    startLine: currentUse.startLine,
                    endLine: i,
                });
                currentUse = null;
            }
        }

        const currentContent = getCurrentContent();
        const contentMatch = trimmed.match(CONTENT_HEADER_RE);
        if (contentMatch && (!currentContent || currentContent.nestingDepth === 0)) {
            contentStack.push({
                defId: contentMatch[1],
                isTemplate: /template/.test(contentMatch[2]),
                startLine: i,
                nestingDepth: 0,
                forNestingDepth: 0,
                bodyLineCount: 0,
                refCount: 0,
            });
        } else if (currentContent?.nestingDepth === 0) {
            const fenceMatch = SYNTAX_FENCE_RE.exec(trimmed);
            if (fenceMatch) {
                contentFence = {
                    kind: fenceMatch[2] as SyntaxFenceKind,
                    length: fenceMatch[1].length,
                };
                mdFenceLines.add(i);
                syntaxFenceLines.set(i, {
                    kind: contentFence.kind,
                    role: "open",
                    length: contentFence.length,
                });
            } else if (FOR_HEADER_RE.test(trimmed)) {
                currentContent.forNestingDepth++;
            } else if (FOR_CLOSE_RE.test(trimmed)) {
                if (currentContent.forNestingDepth > 0) {
                    currentContent.forNestingDepth--;
                }
            } else if (BLOCK_OPENER_RE.test(trimmed)) {
                currentContent.nestingDepth++;
            } else if (
                CLOSE_LINE_RE.test(trimmed) &&
                !ignoreContentCloseLines.has(i)
            ) {
                if (currentContent.forNestingDepth > 0) {
                } else {
                    pushContentBlock(currentContent, i);
                    contentStack.pop();
                }
            } else {
                if (trimmed.length > 0) {
                    currentContent.bodyLineCount++;
                }
                const refMatches = text.match(REF_RE);
                if (refMatches) {
                    currentContent.refCount += refMatches.length;
                }
            }
        } else if (currentContent) {
            if (FOR_HEADER_RE.test(trimmed)) {
                currentContent.forNestingDepth++;
            } else if (FOR_CLOSE_RE.test(trimmed)) {
                if (currentContent.forNestingDepth > 0) {
                    currentContent.forNestingDepth--;
                }
            } else if (BLOCK_OPENER_RE.test(trimmed)) {
                currentContent.nestingDepth++;
            } else if (CLOSE_LINE_RE.test(trimmed)) {
                if (currentContent.nestingDepth > 0) {
                    currentContent.nestingDepth--;
                } else if (currentContent.forNestingDepth > 0) {
                } else {
                    pushContentBlock(currentContent, i);
                    contentStack.pop();
                }
            }
        }
    }

    const defineBlockByLine = buildBlockLineIndex(defineBlocks, doc.lines);
    const htmlCommentBlockByLine = buildBlockLineIndex(htmlCommentBlocks, doc.lines);
    const metaBlockByLine = buildBlockLineIndex(metaBlocks, doc.lines);
    const importByLine = buildLineIndex(importLines, doc.lines);
    const importBlockByLine = buildBlockLineIndex(importLines, doc.lines);
    const useBlockByLine = buildBlockLineIndex(useBlocks, doc.lines);
    const contentBlockByLine = buildBlockLineIndex(contentBlocks, doc.lines);
    const syntaxFenceByLine = new Array<SyntaxFenceLineInfo | undefined>(
        doc.lines + 1,
    );
    for (const [lineNumber, info] of syntaxFenceLines) {
        syntaxFenceByLine[lineNumber] = info;
    }

    const syntaxCommentByTargetId = new Map<string, string>();
    const setAttachedComment = (targetId: string, startLine: number): void => {
        const comment = findAttachedCommentText(doc, startLine, htmlCommentBlockByLine);
        if (comment) {
            syntaxCommentByTargetId.set(targetId, comment);
        }
    };

    for (const block of defineBlocks) {
        setAttachedComment(block.defId, block.startLine);
    }
    for (const block of useBlocks) {
        setAttachedComment(block.useId, block.startLine);
    }
    for (const block of contentBlocks) {
        setAttachedComment(block.defId, block.startLine);
    }
    for (const block of importLines) {
        setAttachedComment(block.namespace, block.startLine);
    }
    const localForIds = new Set<string>();
    for (let lineNumber = 1; lineNumber <= doc.lines; lineNumber += 1) {
        if (
            mdFenceLines.has(lineNumber) ||
            outputFenceLines.has(lineNumber) ||
            syntaxFenceByLine[lineNumber]
        ) {
            continue;
        }
        const forMatch = doc.line(lineNumber).text.trimStart().match(FOR_HEADER_RE);
        if (forMatch) {
            localForIds.add(forMatch[1]);
            setAttachedComment(forMatch[1], lineNumber);
        }
    }

    const localDefIds = new Set<string>();
    const localContentIds = new Set<string>();
    const localUseIds = new Set<string>();
    const localDefPluginMap = new Map<string, string>();

    for (const block of defineBlocks) {
        localDefIds.add(block.defId);
        localDefPluginMap.set(block.defId, block.pluginName);
    }
    for (const block of useBlocks) {
        localDefIds.add(block.useId);
        localUseIds.add(block.useId);
    }
    for (const block of contentBlocks) {
        localDefIds.add(block.defId);
        localContentIds.add(block.defId);
    }

    const hasMetaSnapshot = metaBlocks.some((b) => b.domain === "snapshot");

    // Extract contiguous config / output fence body ranges for JSON-tree rendering.
    const fenceBodyRanges: FenceBodyRange[] = [];
    for (let i = 1; i <= doc.lines; i++) {
        const info = syntaxFenceByLine[i];
        if (!info || info.role !== "body") continue;
        const startLine = i;
        let endLine = i;
        while (
            endLine < doc.lines &&
            syntaxFenceByLine[endLine + 1]?.kind === info.kind &&
            syntaxFenceByLine[endLine + 1]?.role === "body"
        ) {
            endLine++;
        }
        fenceBodyRanges.push({
            kind: info.kind,
            startLine,
            endLine,
        });
        i = endLine + 1;
    }

    return {
        defineBlocks,
        defineBlockByLine,
        htmlCommentBlocks,
        htmlCommentBlockByLine,
        syntaxCommentByTargetId,
        metaBlocks,
        metaBlockByLine,
        importLines,
        importByLine,
        importBlockByLine,
        useBlocks,
        useBlockByLine,
        contentBlocks,
        contentBlockByLine,
        localDefIds,
        localContentIds,
        localForIds,
        localUseIds,
        localDefPluginMap,
        outputFenceLines,
        syntaxFenceByLine,
        mdFenceLines,
        hasMetaSnapshot,
        fenceBodyRanges,
    };
}

export function buildDecorations(
    view: EditorView,
    scan: MagicBlockScan = scanMagicBlocks(view.state.doc),
): DecorationSet {
    const doc = view.state.doc;
    const forceL2 = view.state.field(forceL2Field);
    const selectionDrivesL2 = forceL2 || view.hasFocus;
    const statuses = view.state.field(executionStatusField);
    const errors = view.state.field(executionErrorField);
    const durations = view.state.field(executionDurationField);
    const forStatuses = view.state.field(forExecutionStatusField);
    const forDurations = view.state.field(forExecutionDurationField);
    const inlineRenderedDefinitionIds =
        view.state.field(inlineRenderedDefinitionsField, false) ?? new Set<string>();
    const watchedUnitIds =
        view.state.field(watchedUnitIdsField, false) ?? new Set<string>();

    // Pre-fetch store snapshots once for all $ref resolution in this pass
    const refCtx = view.state.field(refResolveContextField, false) ?? createRefResolveContext();
    const executionStore = usePluginExecutionStore.getState();
    const pluginMetaMap = new Map(
        executionStore.enginePlugins.map((plugin) => [plugin.name, plugin]),
    );
    const documentPath = useEditorStore.getState().getActiveTab()?.filePath ?? null;
    const markdownReferenceDefinitions = collectMarkdownReferenceDefinitions(doc);

    // Pre-scan: collect block ranges once so docChanged only pays for a single pass.
    const {
        defineBlockByLine,
        htmlCommentBlockByLine,
        syntaxCommentByTargetId,
        metaBlockByLine,
        importByLine,
        importBlockByLine,
        useBlockByLine,
        contentBlockByLine,
        localDefIds,
        localContentIds,
        localForIds,
        localUseIds,
        localDefPluginMap,
        syntaxFenceByLine,
        mdFenceLines,
    } = scan;
    const templateContentBlockedUnitIds =
        buildTemplateContentBlockedUnitIds(doc, scan);
    const markdownMathExcludedLines = collectMarkdownMathExcludedLines(scan);
    const markdownMathBlockRanges = collectMarkdownMathBlockRanges(
        doc,
        markdownMathExcludedLines,
    );
    const markdownRichOccupiedLines = new Set<number>();
    for (const range of markdownMathBlockRanges) {
        for (let ln = range.startLine; ln <= range.endLine; ln += 1) {
            markdownRichOccupiedLines.add(ln);
        }
    }
    const markdownRichBlockRanges = collectMarkdownRichBlockRanges(
        doc,
        markdownMathExcludedLines,
        markdownRichOccupiedLines,
    );

    const activeBlockIds = new Set<string>();
    const activeCursorLines = new Set<number>();
    if (selectionDrivesL2) {
        for (const range of view.state.selection.ranges) {
            const headLine = doc.lineAt(range.head).number;
            activeCursorLines.add(headLine);
            const headBlock = defineBlockByLine[headLine];
            if (headBlock) {
                activeBlockIds.add(headBlock.defId);
            }
            if (!range.empty) {
                const anchorLine = doc.lineAt(range.anchor).number;
                activeCursorLines.add(anchorLine);
                const anchorBlock = defineBlockByLine[anchorLine];
                if (anchorBlock) {
                    activeBlockIds.add(anchorBlock.defId);
                }
            }
        }
    }

    const defPluginMap = new Map(localDefPluginMap);
    const cloudWorkerPluginNames =
        view.state.field(cloudWorkerPluginNamesField, false) ?? new Set<string>();
    // Merge Engine-resolved definitions (includes imported ns/id symbols)
    const engineDefs = view.state.field(engineDefinitionsField);
    for (const [id, pluginName] of engineDefs) {
        if (!defPluginMap.has(id)) {
            defPluginMap.set(id, pluginName);
        }
    }

    // Determine active module blocks (cursor inside → L2)
    const activeMetaBlockStarts = new Set<number>();
    const activeImportBlockStarts = new Set<number>();
    const activeUseBlockStarts = new Set<number>();
    const activeContentBlockStarts = new Set<number>();
    const visibleLineSpans = buildVisibleLineSpans(view);
    for (const ln of activeCursorLines) {
        const mb = metaBlockByLine[ln];
        if (mb) activeMetaBlockStarts.add(mb.startLine);
        const ib = importBlockByLine[ln];
        if (ib) activeImportBlockStarts.add(ib.startLine);
        const ub = useBlockByLine[ln];
        if (ub) activeUseBlockStarts.add(ub.startLine);
        const cb = contentBlockByLine[ln];
        if (cb) activeContentBlockStarts.add(cb.startLine);
    }

    // Collect all decorations first, then sort before adding to builder.
    // RangeSetBuilder requires ranges in ascending (from, startSide) order.
    // Line decorations have startSide = -1, mark decorations have higher startSide,
    // so we must NOT interleave them carelessly.
    const decos: { from: number; to: number; deco: Decoration }[] = [];

    let insideDefine = false;
    let currentDefineId: string | null = null;
    let currentDefineStatus: ExecutionStatus = "idle";
    let forDepth = 0;
    let currentForStatus: ForExecutionStatus = "idle";
    const forStack: Array<{ id: string; status: ForExecutionStatus; template: boolean }> = [];
    let insideOutputFence = false;
    let outputFenceLen = 3;

    // ── Fence body JSON-tree rendering (L0) ──
    // In L0 mode replace each contiguous ```config / ```output body with a
    // FenceBodyWidget.  When the cursor is on any line inside the body the
    // range stays raw (L2) so the user can edit the JSON directly.
    const replacedBodyLines = new Set<number>();
    if (!forceL2) {
        for (const range of scan.fenceBodyRanges) {
            const isL2 = rangeHasCursor(
                range.startLine,
                range.endLine,
                activeCursorLines,
            );
            if (isL2) continue;

            // Only render the widget when at least one line of the range is
            // in a visible span (same policy as per-line decorations).
            const isVisible = isLineInVisibleSpans(
                range.startLine,
                visibleLineSpans,
            ) || isLineInVisibleSpans(range.endLine, visibleLineSpans);
            if (!isVisible) continue;

            const bodyLines: string[] = [];
            for (let ln = range.startLine; ln <= range.endLine; ln++) {
                bodyLines.push(doc.line(ln).text);
                replacedBodyLines.add(ln);
            }
            const jsonText = bodyLines.join("\n");

            const firstLine = doc.line(range.startLine);
            decos.push({
                from: firstLine.from,
                to: firstLine.to,
                deco: Decoration.replace({
                    widget: new FenceBodyWidget(jsonText, range.kind),
                }),
            });
            for (let ln = range.startLine + 1; ln <= range.endLine; ln++) {
                const l = doc.line(ln);
                decos.push({
                    from: l.from,
                    to: l.from,
                    deco: Decoration.line({
                        class: "cm-magic-fence-body-hidden",
                    }),
                });
            }
        }
    }

    // ── Markdown rich block lines ──
    // Block-level rich Markdown decorations are provided by markdownRichBlockField
    // so they can legally replace line breaks and collapse the gutter. The ViewPlugin
    // only tracks which lines are owned by those block widgets and skips per-line
    // inline decoration work for them.
    const replacedMarkdownRichLines = new Set<number>();
    if (!forceL2) {
        for (const range of markdownRichBlockRanges) {
            const isL2 = rangeHasCursor(
                range.startLine,
                range.endLine,
                activeCursorLines,
            );
            if (isL2) continue;
            if (
                range.startLine !== range.endLine &&
                !hasFoldedRange(
                    view.state,
                    doc.line(range.startLine).to,
                    doc.line(range.endLine).to,
                )
            ) {
                continue;
            }
            for (let ln = range.startLine; ln <= range.endLine; ln += 1) {
                replacedMarkdownRichLines.add(ln);
            }
        }
    }

    // ── Markdown display math rendering (L0) ──
    // Block math uses normal replace decorations so it can live in the same
    // ViewPlugin decoration set as other markdown syntax.
    const replacedMarkdownMathLines = new Set<number>();
    if (!forceL2) {
        for (const range of markdownMathBlockRanges) {
            const isL2 = rangeHasCursor(
                range.startLine,
                range.endLine,
                activeCursorLines,
            );
            if (isL2) continue;
            if (
                range.startLine !== range.endLine &&
                !hasFoldedRange(
                    view.state,
                    doc.line(range.startLine).to,
                    doc.line(range.endLine).to,
                )
            ) {
                continue;
            }

            const isVisible = isLineInVisibleSpans(
                range.startLine,
                visibleLineSpans,
            ) || isLineInVisibleSpans(range.endLine, visibleLineSpans);
            if (!isVisible) continue;

            const firstLine = doc.line(range.startLine);
            decos.push({
                from: firstLine.from + range.fromIndex,
                to: range.startLine === range.endLine
                    ? firstLine.from + range.toIndex
                    : firstLine.to,
                deco: Decoration.replace({
                    widget: new PrettyMarkdownMathWidget(
                        range.formula,
                        true,
                        firstLine.from + range.fromIndex,
                    ),
                }),
            });

            for (let ln = range.startLine; ln <= range.endLine; ln += 1) {
                replacedMarkdownMathLines.add(ln);
            }
        }
    }

    for (let i = 1; i <= doc.lines; i++) {
        const line = doc.line(i);
        const text = line.text;
        const trimmed = text.trim();
        const trimmedStart = text.trimStart();
        const shouldRenderLine =
            activeCursorLines.has(i) || isLineInVisibleSpans(i, visibleLineSpans);
        const metaBlock = metaBlockByLine[i];
        const importInfo = importByLine[i];
        const importBlock = importBlockByLine[i];
        const useBlock = useBlockByLine[i];
        const contentBlock = contentBlockByLine[i];
        const htmlCommentBlock = htmlCommentBlockByLine[i];
        const syntaxFenceInfo = syntaxFenceByLine[i];
        const isMarkdownRichBlockLine = rangeContainsMarkdownRichBlock(
            markdownRichBlockRanges,
            i,
        );

        // Lines replaced by a FenceBodyWidget in L0 mode skip per-line fence
        // decorations, but non-fence line classes (e.g. content-inner indent)
        // still need to be applied below.
        const isReplacedBodyLine = replacedBodyLines.has(i);
        if (replacedMarkdownRichLines.has(i)) continue;
        if (replacedMarkdownMathLines.has(i)) continue;

        const isContentInnerLine =
            contentBlock !== undefined &&
            i !== contentBlock.startLine &&
            i !== contentBlock.endLine;
        const isInsideTemplateForContainer = forStack.some(
            (entry) => entry.template,
        );

        if (syntaxFenceInfo && shouldRenderLine) {
            const isCursorOnLine = forceL2 || activeCursorLines.has(i);

            // Replaced body lines (L0 JSON-tree widget) skip the fence
            // rail/background line class, but may still carry content-inner
            // indent styling.
            if (!isReplacedBodyLine) {
                decos.push({
                    from: line.from,
                    to: line.from,
                    deco: Decoration.line({ class: getFenceLineClass(syntaxFenceInfo) }),
                });
            }
            if (isContentInnerLine) {
                decos.push({
                    from: line.from,
                    to: line.from,
                    deco: Decoration.line({ class: "cm-magic-content-inner" }),
                });
            }
            if (syntaxFenceInfo.role === "body") {
                // Body lines only get the subtle rail/background; the text stays raw.
                // (In L0 mode the entire body range is already handled by the
                // FenceBodyWidget added before the loop.)
            } else if (!isReplacedBodyLine) {
                if (isCursorOnLine) {
                    decos.push({
                        from: line.from,
                        to: line.to,
                        deco: Decoration.mark({ class: getFenceMarkClass(syntaxFenceInfo) }),
                    });
                } else {
                    decos.push({
                        from: getIndentedSyntaxFrom(line),
                        to: line.to,
                        deco: Decoration.replace({
                            widget: new PrettyFenceWidget(
                                syntaxFenceInfo.kind,
                                syntaxFenceInfo.role,
                                syntaxFenceInfo.length,
                            ),
                        }),
                    });
                }
            }
        }

        if (
            htmlCommentBlock &&
            (
                shouldRenderLine ||
                rangeIntersectsVisibleSpans(
                    htmlCommentBlock.startLine,
                    htmlCommentBlock.endLine,
                    visibleLineSpans,
                )
            ) &&
            !insideOutputFence &&
            !mdFenceLines.has(i)
        ) {
            const isCursorOnLine =
                forceL2 ||
                rangeHasCursor(
                    htmlCommentBlock.startLine,
                    htmlCommentBlock.endLine,
                    activeCursorLines,
                );
            if (isContentInnerLine) {
                decos.push({
                    from: line.from,
                    to: line.from,
                    deco: Decoration.line({ class: "cm-magic-content-body-line" }),
                });
                decos.push({
                    from: line.from,
                    to: line.from,
                    deco: Decoration.line({ class: "cm-magic-content-inner" }),
                });
            }
            decos.push({
                from: line.from,
                to: line.from,
                deco: Decoration.line({ class: "cm-magic-comment-line" }),
            });
            if (isCursorOnLine) {
                decos.push({
                    from: line.from,
                    to: line.to,
                    deco: Decoration.mark({ class: "cm-magic-comment-mark" }),
                });
            } else if (i === htmlCommentBlock.startLine) {
                decos.push({
                    from: getIndentedSyntaxFrom(line),
                    to: line.to,
                    deco: Decoration.replace({
                        widget: new PrettyHtmlCommentWidget(
                            htmlCommentBlock.text,
                            line.from,
                        ),
                    }),
                });
            } else {
                decos.push({
                    from: line.from,
                    to: line.from,
                    deco: Decoration.line({ class: "cm-magic-comment-line-hidden" }),
                });
            }
            continue;
        }

        // ── Module directives (checked BEFORE ::define to avoid false matches) ──

        // ::meta[...] block lines
        if (metaBlock) {
            if (!shouldRenderLine) continue;
            const isActive =
                forceL2 || activeMetaBlockStarts.has(metaBlock.startLine);
            // Line background for all lines in meta block
            decos.push({
                from: line.from,
                to: line.from,
                deco: Decoration.line({ class: "cm-magic-meta-line" }),
            });
            if (i === metaBlock.startLine) {
                if (isActive) {
                    // L2: raw syntax with mark
                    decos.push({
                        from: line.from,
                        to: line.to,
                        deco: Decoration.mark({ class: "cm-magic-meta-head" }),
                    });
                } else {
                    // L0: pretty widget on header line (fold system collapses the rest)
                    decos.push({
                        from: getIndentedSyntaxFrom(line),
                        to: line.to,
                        deco: Decoration.replace({
                            widget: new PrettyMetaWidget(metaBlock.domain, metaBlock.summary),
                        }),
                    });
                }
                if (!isActive) {
                    // Config button (⚙) — only in L0
                    decos.push({
                        from: line.to,
                        to: line.to,
                        deco: Decoration.widget({
                            widget: new MetaButtonGroupWidget(metaBlock.domain),
                            side: ACTION_WIDGET_SIDE,
                        }),
                    });
                }
            } else if (i === metaBlock.endLine) {
                if (isActive) {
                    decos.push({
                        from: line.from,
                        to: line.to,
                        deco: Decoration.mark({ class: "cm-magic-meta-close" }),
                    });
                } else {
                    decos.push({
                        from: getIndentedSyntaxFrom(line),
                        to: line.to,
                        deco: Decoration.replace({
                            widget: new PrettyEndWidget("meta", metaBlock.domain),
                        }),
                    });
                }
            }
            if (isContentInnerLine) {
                decos.push({
                    from: line.from,
                    to: line.from,
                    deco: Decoration.line({ class: "cm-magic-content-inner" }),
                });
            }
            continue;
        }

        // ::import{...} single-line directive
        if (importInfo) {
            if (!shouldRenderLine) continue;
            const impInfo = importInfo;
            const isCursorOnLine = forceL2 || activeCursorLines.has(i);
            // Line background
            decos.push({
                from: line.from,
                to: line.from,
                deco: Decoration.line({
                    class: `cm-magic-import-line${isCursorOnLine ? " cm-magic-import-active" : ""}`,
                }),
            });
            if (isCursorOnLine) {
                // L2: raw syntax with mark decoration
                decos.push({
                    from: line.from,
                    to: line.to,
                    deco: Decoration.mark({ class: "cm-magic-import-head" }),
                });
            } else {
                // L0: pretty widget
                decos.push({
                    from: getIndentedSyntaxFrom(line),
                    to: line.to,
                    deco: Decoration.replace({
                        widget: new PrettyImportWidget(
                            impInfo.namespace,
                            impInfo.source,
                            buildImportDisplaySummary(
                                impInfo,
                                executionStore.importExecutions.get(impInfo.namespace),
                            ),
                            watchedUnitIds.has(impInfo.namespace),
                        ),
                    }),
                });
            }
            const importStatus: ExecutionStatus =
                statuses.get(impInfo.namespace) ?? "idle";
            decos.push({
                from: line.to,
                to: line.to,
                deco: Decoration.widget({
                    widget: new ImportButtonGroupWidget(
                        impInfo.namespace,
                        impInfo.source,
                        importStatus,
                    ),
                    side: ACTION_WIDGET_SIDE,
                }),
            });
            if (!isCursorOnLine) {
                if (importStatus !== "idle" && importStatus !== "cached") {
                    decos.push({
                        from: line.to,
                        to: line.to,
                        deco: Decoration.widget({
                            widget: getCachedStatusDotWidget(importStatus),
                            side: STATUS_WIDGET_SIDE,
                        }),
                    });
                }
                if (importStatus !== "idle" && importStatus !== "cached") {
                    decos.push({
                        from: line.to,
                        to: line.to,
                        deco: Decoration.widget({
                            widget: getCachedStatusBadgeWidget(
                                importStatus,
                                durations.get(impInfo.namespace) ?? null,
                            ),
                            side: STATUS_WIDGET_SIDE,
                        }),
                    });
                }
            }
            // Inline error message for import
            const importErrorMsg = errors.get(impInfo.namespace);
            if (importErrorMsg) {
                decos.push({
                    from: line.to,
                    to: line.to,
                    deco: Decoration.widget({
                        widget: new ErrorMessageWidget(impInfo.namespace, importErrorMsg),
                        side: 1,
                    }),
                });
            }
            if (isContentInnerLine) {
                decos.push({
                    from: line.from,
                    to: line.from,
                    deco: Decoration.line({ class: "cm-magic-content-inner" }),
                });
            }
            continue;
        }

        if (importBlock && i === importBlock.endLine && importBlock.endLine !== importBlock.startLine) {
            if (!shouldRenderLine) continue;
            const isActive = forceL2 || activeImportBlockStarts.has(importBlock.startLine);
            decos.push({
                from: line.from,
                to: line.from,
                deco: Decoration.line({
                    class: `cm-magic-import-line${isActive ? " cm-magic-import-active" : ""}`,
                }),
            });
            if (isActive) {
                decos.push({
                    from: line.from,
                    to: line.to,
                    deco: Decoration.mark({ class: "cm-magic-import-close" }),
                });
            } else {
                decos.push({
                    from: getIndentedSyntaxFrom(line),
                    to: line.to,
                    deco: Decoration.replace({
                        widget: new PrettyEndWidget("import", importBlock.namespace),
                    }),
                });
            }
            continue;
        }

        // ::use[id]{from="..."} block lines
        if (useBlock) {
            if (i === useBlock.startLine) insideOutputFence = false; // reset on block boundary
            if (!shouldRenderLine) continue;
            const isActive = forceL2 || activeUseBlockStarts.has(useBlock.startLine);
            // Line background for all lines in use block
            decos.push({
                from: line.from,
                to: line.from,
                deco: Decoration.line({
                    class: `cm-magic-use-line${isActive ? " cm-magic-use-active" : ""}`,
                }),
            });
            if (i === useBlock.startLine) {
                // Use execution status
                const isScopedInsideFor = forDepth > 0;
                const isTemplateExecutionBlocked =
                    isInsideTemplateForContainer ||
                    templateContentBlockedUnitIds.has(useBlock.useId);
                const isExecutionBlockedByContainer =
                    isScopedInsideFor || isTemplateExecutionBlocked;
                const useStatus: ExecutionStatus =
                    isExecutionBlockedByContainer ? "idle" : (statuses.get(useBlock.useId) ?? "idle");
                const useCanRun =
                    !isTemplateExecutionBlocked &&
                    (
                        forDepth === 0 ||
                        !lineRangeUsesLoopVariableRef(
                            doc,
                            useBlock.startLine,
                            useBlock.endLine,
                        )
                    );
                const sourcePluginName = defPluginMap.get(useBlock.source) ?? null;
                const sourceKind =
                    localForIds.has(useBlock.source)
                        ? "for"
                        : localContentIds.has(useBlock.source)
                        ? "content"
                        : localUseIds.has(useBlock.source)
                        ? "use"
                        : defPluginMap.has(useBlock.source)
                        ? "definition"
                        : "content";
                if (isActive) {
                    // L2: raw syntax with mark
                    decos.push({
                        from: line.from,
                        to: line.to,
                        deco: Decoration.mark({ class: "cm-magic-use-head" }),
                    });
                } else {
                    // L0: pretty widget on header line (fold system collapses the rest)
                    decos.push({
                        from: getIndentedSyntaxFrom(line),
                        to: line.to,
                        deco: Decoration.replace({
                            widget: new PrettyUseWidget(
                                useBlock.useId,
                                useBlock.source,
                                buildUseDisplaySummary(
                                    useBlock.modifiers,
                                    executionStore.useExecutions.get(useBlock.useId),
                                ),
                                sourceKind,
                                !localDefIds.has(useBlock.source) && !localForIds.has(useBlock.source),
                                sourceKind === "definition" ? sourcePluginName : null,
                                useBlock.isTemplate,
                                watchedUnitIds.has(useBlock.useId),
                            ),
                        }),
                    });
                }
                decos.push({
                    from: line.to,
                    to: line.to,
                    deco: Decoration.widget({
                        widget: new UseButtonGroupWidget(
                            useBlock.useId,
                            useStatus,
                            sourcePluginName,
                            sourceKind,
                            useCanRun,
                            true,
                            inlineRenderedDefinitionIds.has(useBlock.useId),
                            useBlock.isTemplate,
                        ),
                        side: ACTION_WIDGET_SIDE,
                    }),
                });
                if (!isActive) {
                    if (useStatus !== "idle" && useStatus !== "cached") {
                        decos.push({
                            from: line.to,
                            to: line.to,
                            deco: Decoration.widget({
                                widget: getCachedStatusDotWidget(useStatus),
                                side: STATUS_WIDGET_SIDE,
                            }),
                        });
                    }
                    if (useStatus !== "idle" && useStatus !== "cached") {
                        decos.push({
                            from: line.to,
                            to: line.to,
                            deco: Decoration.widget({
                                widget: getCachedStatusBadgeWidget(
                                    useStatus,
                                    durations.get(useBlock.useId) ?? null,
                                ),
                                side: STATUS_WIDGET_SIDE,
                            }),
                        });
                    }
                }
                // Inline error message for use block
                const useErrorMsg = isScopedInsideFor ? undefined : errors.get(useBlock.useId);
                if (useErrorMsg) {
                    decos.push({
                        from: line.to,
                        to: line.to,
                        deco: Decoration.widget({
                            widget: new ErrorMessageWidget(useBlock.useId, useErrorMsg),
                            side: 1,
                        }),
                    });
                }
            } else if (i === useBlock.endLine) {
                if (isActive) {
                    decos.push({
                        from: line.from,
                        to: line.to,
                        deco: Decoration.mark({ class: "cm-magic-use-close" }),
                    });
                } else {
                    decos.push({
                        from: getIndentedSyntaxFrom(line),
                        to: line.to,
                        deco: Decoration.replace({
                            widget: new PrettyEndWidget("use", useBlock.useId),
                        }),
                    });
                }
            }
            if (isContentInnerLine) {
                decos.push({
                    from: line.from,
                    to: line.from,
                    deco: Decoration.line({ class: "cm-magic-content-inner" }),
                });
            }
            continue;
        }

        // ::content[id] block lines
        if (contentBlock) {
            if (i === contentBlock.startLine) insideOutputFence = false; // reset on block boundary
            if (!shouldRenderLine) continue;
            const isActive =
                forceL2 || activeContentBlockStarts.has(contentBlock.startLine);
            if (i === contentBlock.startLine) {
                const isScopedInsideFor = forDepth > 0;
                const contentExecution = isScopedInsideFor
                    ? undefined
                    : executionStore.contentExecutions.get(contentBlock.defId);
                // Header line — always gets line background
                decos.push({
                    from: line.from,
                    to: line.from,
                    deco: Decoration.line({
                        class: `cm-magic-content-line${isActive ? " cm-magic-content-active" : ""}`,
                    }),
                });
                if (isActive) {
                    decos.push({
                        from: line.from,
                        to: line.to,
                        deco: Decoration.mark({ class: "cm-magic-content-head" }),
                    });
                } else {
                    decos.push({
                        from: getIndentedSyntaxFrom(line),
                        to: line.to,
                        deco: Decoration.replace({
                            widget: new PrettyContentDefineWidget(
                                contentBlock.defId,
                                contentBlock.isTemplate,
                                buildContentDisplaySummary(
                                    contentBlock.summary,
                                    contentExecution,
                                ),
                                watchedUnitIds.has(contentBlock.defId),
                            ),
                        }),
                    });
                }
                const isTemplateExecutionBlocked =
                    isInsideTemplateForContainer ||
                    templateContentBlockedUnitIds.has(contentBlock.defId);
                const contentStatus: ExecutionStatus =
                    isScopedInsideFor || isTemplateExecutionBlocked
                        ? "idle"
                        : (statuses.get(contentBlock.defId) ?? "idle");
                const contentCanRun =
                    !isTemplateExecutionBlocked &&
                    (
                        forDepth === 0 ||
                        !lineRangeUsesLoopVariableRef(
                            doc,
                            contentBlock.startLine,
                            contentBlock.endLine,
                        )
                    );
                decos.push({
                    from: line.to,
                    to: line.to,
                    deco: Decoration.widget({
                        widget: new ContentButtonGroupWidget(
                            contentBlock.defId,
                            contentStatus,
                            contentCanRun,
                            true,
                            inlineRenderedDefinitionIds.has(contentBlock.defId),
                            contentBlock.isTemplate,
                        ),
                        side: ACTION_WIDGET_SIDE,
                    }),
                });
                if (!isActive) {
                    if (contentStatus !== "idle") {
                        decos.push({
                            from: line.to,
                            to: line.to,
                            deco: Decoration.widget({
                                widget: getCachedStatusDotWidget(contentStatus),
                                side: STATUS_WIDGET_SIDE,
                            }),
                        });
                    }
                    if (contentStatus !== "idle") {
                        decos.push({
                            from: line.to,
                            to: line.to,
                            deco: Decoration.widget({
                                widget: getCachedStatusBadgeWidget(
                                    contentStatus,
                                    durations.get(contentBlock.defId) ?? null,
                                ),
                                side: STATUS_WIDGET_SIDE,
                            }),
                        });
                    }
                }
                // Inline error message for content block
                const contentErrorMsg = isScopedInsideFor ? undefined : errors.get(contentBlock.defId);
                if (contentErrorMsg) {
                    decos.push({
                        from: line.to,
                        to: line.to,
                        deco: Decoration.widget({
                            widget: new ErrorMessageWidget(
                                contentBlock.defId,
                                contentErrorMsg,
                            ),
                            side: 1,
                        }),
                    });
                }
                continue;
            } else if (i === contentBlock.endLine) {
                // Close :: line — always gets line background
                decos.push({
                    from: line.from,
                    to: line.from,
                    deco: Decoration.line({ class: "cm-magic-content-line" }),
                });
                if (isActive) {
                    decos.push({
                        from: line.from,
                        to: line.to,
                        deco: Decoration.mark({ class: "cm-magic-content-close" }),
                    });
                } else {
                    decos.push({
                        from: getIndentedSyntaxFrom(line),
                        to: line.to,
                        deco: Decoration.replace({
                            widget: new PrettyEndWidget("content", contentBlock.defId),
                        }),
                    });
                }
                continue;
            } else {
                // Body lines — add border styling + nesting indentation, then FALL THROUGH
                // to allow inner ::define, ::render, $ref{} to be processed.
                // DESIGN NOTE: This intentional fall-through means a single line may accumulate
                // both `cm-magic-content-inner` and e.g. `cm-magic-define-line` backgrounds.
                // CSS specificity must ensure proper layering (content-inner is a lower-priority base).
                decos.push({
                    from: line.from,
                    to: line.from,
                    deco: Decoration.line({ class: "cm-magic-content-body-line" }),
                });
                decos.push({
                    from: line.from,
                    to: line.from,
                    deco: Decoration.line({ class: "cm-magic-content-inner" }),
                });
                // DO NOT continue — fall through to process inner syntax
            }
        }

        // ── Existing directive handling ──

        // ::define[id]{plugin="name" ...}
        const defineMatch = trimmedStart.match(DEFINE_RE);
        if (defineMatch) {
            insideDefine = true;
            insideOutputFence = false;
            const defId = defineMatch[1];
            currentDefineId = defId;
            const defAttrs = parseAttrs(defineMatch[2]);
            const pluginName = defAttrs.plugin ?? "";
            const isCloudWorkerPlugin = cloudWorkerPluginNames.has(pluginName);
            const status = statuses.get(defId) ?? "idle";
            const canInlineRender = true; // All blocks can use ::render:: syntax
            const inlineRendered =
                canInlineRender && inlineRenderedDefinitionIds.has(defId);
            currentDefineStatus = status;
            if (!shouldRenderLine) continue;
            const isActive = forceL2 || activeBlockIds.has(defId);

            // Line decoration (background + left border) — always applied
            decos.push({
                from: line.from,
                to: line.from,
                deco: Decoration.line({
                    class: `${statusBgClass(status)}${isActive ? " cm-magic-define-active" : ""}`,
                }),
            });

            if (isActive) {
                // L2: raw syntax with mark decoration
                decos.push({ from: line.from, to: line.to, deco: defineHeadMark });
            } else {
                // L0: pretty widget replacing the raw syntax
                decos.push({
                    from: getIndentedSyntaxFrom(line),
                    to: line.to,
                    deco: Decoration.replace({
                        widget: new PrettyDefineWidget(
                            defId,
                            pluginName,
                            buildDefineDisplaySummary(
                                defineBlockByLine[i]?.summary ??
                                    tr("syntax.summary.runPlugin", { pluginName }),
                                pluginName,
                                pluginMetaMap.get(pluginName),
                            ),
                            defineBlockByLine[i]?.isTemplate ?? /template="true"/.test(trimmedStart),
                            isCloudWorkerPlugin,
                            watchedUnitIds.has(defId),
                        ),
                    }),
                });
            }

            const insideContent = isContentInnerLine;
            const isTemplateExecutionBlocked =
                isInsideTemplateForContainer ||
                templateContentBlockedUnitIds.has(defId);
            decos.push({
                from: line.to,
                to: line.to,
                deco: Decoration.widget({
                    widget: new ButtonGroupWidget(
                        defId,
                        pluginName,
                        status,
                        forDepth > 0 || isTemplateExecutionBlocked,
                        canInlineRender,
                        inlineRendered,
                        defineBlockByLine[i]?.isTemplate ?? /template="true"/.test(trimmedStart),
                    ),
                    side: ACTION_WIDGET_SIDE,
                }),
            });

            if (!isActive) {
                if (status !== "idle" && status !== "cached") {
                    decos.push({
                        from: line.to,
                        to: line.to,
                        deco: Decoration.widget({
                            widget: getCachedStatusDotWidget(status),
                            side: STATUS_WIDGET_SIDE,
                        }),
                    });
                }

                if (status !== "idle" && status !== "cached") {
                    decos.push({
                        from: line.to,
                        to: line.to,
                        deco: Decoration.widget({
                            widget: getCachedStatusBadgeWidget(status, durations.get(defId) ?? null),
                            side: STATUS_WIDGET_SIDE,
                        }),
                    });
                }
            }

            // Inline error message — always visible regardless of L0/L2
            const errorMsg = errors.get(defId);
            if (errorMsg) {
                decos.push({
                    from: line.to,
                    to: line.to,
                    deco: Decoration.widget({
                        widget: new ErrorMessageWidget(defId, errorMsg),
                        side: 1,
                    }),
                });
            }
            if (insideContent) {
                decos.push({
                    from: line.from,
                    to: line.from,
                    deco: Decoration.line({ class: "cm-magic-content-inner" }),
                });
            }
            continue;
        }

        // Standalone :: (define block close)
        if (DEFINE_CLOSE_RE.test(trimmed) && insideDefine) {
            if (!shouldRenderLine) {
                insideDefine = false;
                currentDefineId = null;
                insideOutputFence = false;
                currentDefineStatus = "idle";
                continue;
            }
            const isActive =
                forceL2 || (currentDefineId ? activeBlockIds.has(currentDefineId) : false);
            const closingDefineId = currentDefineId;
            decos.push({
                from: line.from,
                to: line.from,
                deco: Decoration.line({ class: statusBgClass(currentDefineStatus) }),
            });
            insideDefine = false;
            currentDefineId = null;
            insideOutputFence = false;
            currentDefineStatus = "idle";
            if (isActive) {
                decos.push({ from: line.from, to: line.to, deco: defineCloseMark });
            } else {
                decos.push({
                    from: getIndentedSyntaxFrom(line),
                    to: line.to,
                    deco: Decoration.replace({
                        widget: new PrettyEndWidget("define", closingDefineId),
                    }),
                });
            }
            {
                const closeContentBlock = contentBlockByLine[i];
                if (
                    closeContentBlock &&
                    i !== closeContentBlock.startLine &&
                    i !== closeContentBlock.endLine
                ) {
                    decos.push({
                        from: line.from,
                        to: line.from,
                        deco: Decoration.line({ class: "cm-magic-content-inner" }),
                    });
                }
            }
            continue;
        }

        if (!shouldRenderLine) {
            // Track output fence state across ALL block types (define, use, content, for).
            // Output fences contain opaque serialized data — $ref patterns there are literal text.
            if (trimmed.startsWith("```")) {
                if (insideOutputFence) {
                    // Check closing fence: must match the opening fence length
                    if (trimmed.length === outputFenceLen && trimmed === "`".repeat(outputFenceLen)) {
                        insideOutputFence = false;
                    }
                } else if (trimmed.startsWith("```output")) {
                    insideOutputFence = true;
                    outputFenceLen = (trimmed.match(/^(`{3,})output/) ?? ["", "```"])[1].length;
                }
            }

            const skippedForMatch = trimmedStart.match(FOR_HEADER_RE);
            if (skippedForMatch) {
                insideOutputFence = false; // reset on block boundary
                const status = forStatuses.get(skippedForMatch[1]) ?? "idle";
                forStack.push({
                    id: skippedForMatch[1],
                    status,
                    template: /template="true"/.test(skippedForMatch[2]),
                });
                forDepth++;
                currentForStatus = status;
                continue;
            }

            if (FOR_CLOSE_RE.test(trimmed)) {
                insideOutputFence = false; // reset on block boundary
                forStack.pop();
                forDepth = Math.max(0, forDepth - 1);
                currentForStatus = forStack[forStack.length - 1]?.status ?? "idle";
            }
            continue;
        }

        // Track output fence boundaries inside ALL block types (BEFORE padding decision)
        let isOutputFenceLine = false;
        {
            const trimmedForFence = text.trimStart();
            if (trimmedForFence.startsWith("```")) {
                if (insideOutputFence) {
                    // Check closing fence: must match the opening fence length
                    if (trimmedForFence.trim().length === outputFenceLen && trimmedForFence.trim() === "`".repeat(outputFenceLen)) {
                        isOutputFenceLine = true; // closing ``` of output fence
                        insideOutputFence = false;
                    }
                } else if (trimmedForFence.startsWith("```output")) {
                    isOutputFenceLine = true; // opening ```output
                    insideOutputFence = true;
                    outputFenceLen = (trimmedForFence.match(/^(`{3,})output/) ?? ["", "```"])[1].length;
                }
            }
        }

        // Inside a define block — add status-aware background
        if (insideDefine) {
            decos.push({
                from: line.from,
                to: line.from,
                deco: Decoration.line({ class: statusBgClass(currentDefineStatus) }),
            });
            // Content-inner indentation for define content inside content blocks
            if (!insideOutputFence && !isOutputFenceLine) {
                if (isContentInnerLine) {
                    decos.push({
                        from: line.from,
                        to: line.from,
                        deco: Decoration.line({ class: "cm-magic-content-inner" }),
                    });
                }
            }
        }
        // Inside a for block (but not inside a nested define) — add for background
        // Skip lines that have their own handling (::for header and ::endfor)
        else if (
            forDepth > 0 &&
            !FOR_HEADER_RE.test(trimmed) &&
            !FOR_CLOSE_RE.test(trimmed)
        ) {
            decos.push({
                from: line.from,
                to: line.from,
                deco: Decoration.line({ class: getForLineClass(currentForStatus) }),
            });
            if (isContentInnerLine) {
                decos.push({
                    from: line.from,
                    to: line.from,
                    deco: Decoration.line({ class: "cm-magic-content-inner" }),
                });
            }
        }

        // ::for[id]{each="var" of="expr"} — for-block header
        const forMatch = trimmedStart.match(FOR_HEADER_RE);
        if (forMatch) {
            insideOutputFence = false; // reset on block boundary
            const forId = forMatch[1];
            // Parse attributes from the {…} part
            const attrsStr = forMatch[2];
            const eachMatch = attrsStr.match(/each="([^"]*)"/);
            const ofMatch = attrsStr.match(/of="([^"]*)"/);
            const each = eachMatch?.[1] ?? "item";
            const ofExpr = ofMatch?.[1] ?? "";
            const parentForDepth = forDepth;
            const isForTemplate = /template="true"/.test(attrsStr);

            const isCursorOnLine = forceL2 || activeCursorLines.has(i);
            const forStatus = forStatuses.get(forId) ?? "idle";
            forDepth++;
            forStack.push({ id: forId, status: forStatus, template: isForTemplate });
            currentForStatus = forStatus;

            // Line decoration (status-aware background)
            decos.push({
                from: line.from,
                to: line.from,
                deco: Decoration.line({
                    class: `${getForLineClass(currentForStatus)}${isCursorOnLine ? " cm-magic-for-active" : ""}`,
                }),
            });

            if (!isCursorOnLine) {
                // L0: pretty widget
                decos.push({
                    from: getIndentedSyntaxFrom(line),
                    to: line.to,
                    deco: Decoration.replace({
                        widget: new PrettyForWidget(
                            forId,
                            each,
                            ofExpr,
                            buildForSummary(
                                attrsStr,
                                ofExpr,
                                executionStore.forExecutions.get(forId),
                            ),
                            isForTemplate,
                            watchedUnitIds.has(forId),
                        ),
                    }),
                });
            }

            const isTemplateExecutionBlocked =
                isInsideTemplateForContainer ||
                templateContentBlockedUnitIds.has(forId);
            decos.push({
                from: line.to,
                to: line.to,
                deco: Decoration.widget({
                    widget: new ForButtonGroupWidget(
                        forId,
                        currentForStatus,
                        !isTemplateExecutionBlocked &&
                            (
                                parentForDepth === 0 ||
                                !lineRangeUsesLoopVariableRef(
                                    doc,
                                    i,
                                    findForCloseLine(doc, i),
                                )
                            ),
                        true,
                        inlineRenderedDefinitionIds.has(forId),
                        isForTemplate,
                    ),
                    side: ACTION_WIDGET_SIDE,
                }),
            });

            if (!isCursorOnLine) {
                if (currentForStatus !== "idle") {
                    decos.push({
                        from: line.to,
                        to: line.to,
                        deco: Decoration.widget({
                            widget: getCachedStatusDotWidget(currentForStatus as ExecutionStatus),
                            side: STATUS_WIDGET_SIDE,
                        }),
                    });
                }

                if (currentForStatus !== "idle") {
                    decos.push({
                        from: line.to,
                        to: line.to,
                        deco: Decoration.widget({
                            widget: getCachedStatusBadgeWidget(
                                currentForStatus as ExecutionStatus,
                                forDurations.get(forId) ?? null,
                            ),
                            side: STATUS_WIDGET_SIDE,
                        }),
                    });
                }
            }
            // Inline error message for for-block
            const forErrorMsg = errors.get(forId);
            if (forErrorMsg) {
                decos.push({
                    from: line.to,
                    to: line.to,
                    deco: Decoration.widget({
                        widget: new ErrorMessageWidget(forId, forErrorMsg),
                        side: 1,
                    }),
                });
            }
            continue;
        }

        // ::endfor — for-block close
        if (FOR_CLOSE_RE.test(trimmed)) {
            insideOutputFence = false; // reset on block boundary
            const isCursorOnLine = forceL2 || activeCursorLines.has(i);
            const closingFor = forStack.pop();
            decos.push({
                from: line.from,
                to: line.from,
                deco: Decoration.line({ class: getForLineClass(currentForStatus) }),
            });
            forDepth = Math.max(0, forDepth - 1);
            currentForStatus = forStack[forStack.length - 1]?.status ?? "idle";
            if (!isCursorOnLine) {
                decos.push({
                    from: getIndentedSyntaxFrom(line),
                    to: line.to,
                    deco: Decoration.replace({
                        widget: new PrettyEndWidget("for", closingFor?.id),
                    }),
                });
            }
            continue;
        }

        const markdownReferenceDefinition = parseMarkdownReferenceDefinitionSyntax(text);
        if (
            shouldRenderLine &&
            !insideOutputFence &&
            !mdFenceLines.has(i) &&
            markdownReferenceDefinition
        ) {
            const from = line.from + markdownReferenceDefinition.fromIndex;
            const to = line.from + markdownReferenceDefinition.toIndex;
            const isReferenceDefinitionL2 =
                forceL2 || (view.hasFocus && activeCursorLines.has(i));
            if (isReferenceDefinitionL2) {
                decos.push({
                    from,
                    to,
                    deco: Decoration.mark({
                        class: "cm-magic-markdown-refdef-raw",
                    }),
                });
            } else {
                decos.push({
                    from,
                    to,
                    deco: Decoration.replace({
                        widget: new PrettyMarkdownReferenceDefinitionWidget(
                            markdownReferenceDefinition.id,
                            markdownReferenceDefinition.url,
                            markdownReferenceDefinition.title,
                            documentPath,
                            from,
                        ),
                    }),
                });
                continue;
            }
        }

        let markdownLinkMatches: ReturnType<typeof findMarkdownLinkSyntaxMatches> = [];
        const renderedMarkdownLinkMatches: typeof markdownLinkMatches = [];
        let markdownMathMatches: ReturnType<typeof findMarkdownInlineMathSyntaxMatches> = [];
        let markdownRichInlineMatches: ReturnType<typeof findMarkdownRichInlineMatches> = [];
        const renderedMarkdownRichInlineMatches: typeof markdownRichInlineMatches = [];
        if (
            shouldRenderLine &&
            !insideOutputFence &&
            !mdFenceLines.has(i) &&
            !isMarkdownRichBlockLine
        ) {
            markdownLinkMatches = findMarkdownLinkSyntaxMatches(
                text,
                markdownReferenceDefinitions,
            );
            for (const linkMatch of markdownLinkMatches) {
                const from = line.from + linkMatch.fromIndex;
                const to = line.from + linkMatch.toIndex;
                const isLinkL2 =
                    forceL2 ||
                    (view.hasFocus && selectionTouchesRange(view, from, to));
                if (!isLinkL2) {
                    renderedMarkdownLinkMatches.push(linkMatch);
                    decos.push({
                        from,
                        to,
                        deco: Decoration.replace({
                            widget: new PrettyMarkdownLinkWidget(
                                linkMatch.label,
                                linkMatch.url,
                                documentPath,
                                from,
                                {
                                    syntaxKind: linkMatch.kind,
                                    title: linkMatch.title,
                                },
                            ),
                        }),
                    });
                }
            }
        }

        if (
            shouldRenderLine &&
            !insideOutputFence &&
            !mdFenceLines.has(i) &&
            !isMarkdownRichBlockLine &&
            !rangeContainsMarkdownMathBlock(markdownMathBlockRanges, i)
        ) {
            markdownMathMatches = findMarkdownInlineMathSyntaxMatches(text);
            for (const mathMatch of markdownMathMatches) {
                if (
                    rangeContainsMarkdownLink(
                        renderedMarkdownLinkMatches,
                        mathMatch.fromIndex,
                    )
                ) {
                    continue;
                }

                const from = line.from + mathMatch.fromIndex;
                const to = line.from + mathMatch.toIndex;
                const isMathL2 =
                    forceL2 ||
                    (view.hasFocus && selectionTouchesRange(view, from, to));
                if (isMathL2) {
                    decos.push({
                        from,
                        to,
                        deco: Decoration.mark({
                            class: "cm-magic-markdown-math-raw",
                        }),
                    });
                } else {
                    decos.push({
                        from,
                        to,
                        deco: Decoration.replace({
                            widget: new PrettyMarkdownMathWidget(
                                mathMatch.formula,
                                false,
                                from,
                            ),
                        }),
                    });
                }
            }
        }

        if (
            shouldRenderLine &&
            !insideOutputFence &&
            !mdFenceLines.has(i) &&
            !isMarkdownRichBlockLine &&
            !rangeContainsMarkdownMathBlock(markdownMathBlockRanges, i)
        ) {
            markdownRichInlineMatches = findMarkdownRichInlineMatches(text);
            for (const richMatch of markdownRichInlineMatches) {
                if (
                    rangeContainsMarkdownLink(
                        renderedMarkdownLinkMatches,
                        richMatch.fromIndex,
                    ) ||
                    rangeContainsMarkdownMath(markdownMathMatches, richMatch.fromIndex)
                ) {
                    continue;
                }
                if (
                    richMatch.raw.includes("$ref{") ||
                    richMatch.raw.includes("::render[") ||
                    richMatch.raw.includes("](") ||
                    richMatch.raw.includes("][")
                ) {
                    continue;
                }

                const from = line.from + richMatch.fromIndex;
                const to = line.from + richMatch.toIndex;
                const isRichInlineL2 =
                    forceL2 ||
                    (view.hasFocus && selectionTouchesRange(view, from, to));
                if (isRichInlineL2) {
                    decos.push({
                        from,
                        to,
                        deco: Decoration.mark({
                            class: "cm-magic-markdown-rich-raw",
                        }),
                    });
                } else {
                    renderedMarkdownRichInlineMatches.push(richMatch);
                    decos.push({
                        from,
                        to,
                        deco: Decoration.replace({
                            widget: new PrettyMarkdownInlineRichWidget(
                                richMatch.kind,
                                richMatch.text,
                                from,
                                richMatch.id ?? null,
                            ),
                        }),
                    });
                }
            }
        }

        // ::render[id]:: or ::render[id]{attrs}:: — can appear multiple times per line
        // Skip inside output fences — content there is opaque serialized data.
        // Skip inside markdown fenced code blocks — content there is literal example text.
        if (text.includes("::render[") && !insideOutputFence && !mdFenceLines.has(i)) {
            const isCursorOnLine = forceL2 || activeCursorLines.has(i);
            // Use global regex with matchAll to find ALL render directives on the line
            for (const renderMatch of text.matchAll(INLINE_RENDER_RE)) {
                if (renderMatch.index == null) continue;
                // Skip if preceded by backslash or inside backtick code span
                const idx = renderMatch.index;
                const escapedByBackslash = idx > 0 && text[idx - 1] === "\\";
                if (escapedByBackslash || isInsideCodeSpan(text, idx)) continue;
                if (rangeContainsMarkdownMath(markdownMathMatches, idx)) continue;
                if (rangeContainsMarkdownRichInline(renderedMarkdownRichInlineMatches, idx)) continue;

                const from = line.from + idx;
                const to = from + renderMatch[0].length;
                const renderId = renderMatch[1];
                // Detect block vs inline: block = render is the only content on the line
                const isBlockRender = text.trim() === renderMatch[0];

                // Determine render status for line decoration
                // Check both regular definitions and for-blocks
                const renderStatus: ExecutionStatus =
                    statuses.get(renderId) ??
                    (forStatuses.get(renderId) as ExecutionStatus | undefined) ??
                    "idle";
                const renderDuration =
                    durations.get(renderId) ?? forDurations.get(renderId) ?? null;
                const renderDisplay = getRenderDisplayInfo(
                    renderId,
                    localDefIds,
                    localContentIds,
                    defPluginMap,
                    syntaxCommentByTargetId,
                );

                if (isCursorOnLine) {
                    // L2: raw syntax with mark decoration
                    decos.push({ from, to, deco: renderMark });
                } else if (isBlockRender) {
                    // L0 block: full-line pretty widget (same style as define)
                    const renderLineClass =
                        renderStatus === "running"
                            ? "cm-magic-render-line cm-magic-render-line-running"
                            : renderStatus === "success"
                                ? "cm-magic-render-line cm-magic-render-line-success"
                                : renderStatus === "error"
                                    ? "cm-magic-render-line cm-magic-render-line-error"
                                    : "cm-magic-render-line";
                    decos.push({
                        from: line.from,
                        to: line.from,
                        deco: Decoration.line({ class: renderLineClass }),
                    });
                    decos.push({
                        from,
                        to,
                        deco: Decoration.replace({
                            widget: new PrettyRenderBlockWidget(
                                renderId,
                                renderDisplay.targetKind,
                                renderDisplay.summary,
                                !localDefIds.has(renderId),
                                renderDisplay.pluginName,
                                renderDisplay.comment,
                            ),
                        }),
                    });
                } else {
                    // L0 inline: compact pill widget
                    decos.push({
                        from,
                        to,
                        deco: Decoration.replace({
                            widget: new PrettyRenderInlineWidget(
                                renderId,
                                renderDisplay.targetKind,
                                renderDisplay.summary,
                                !localDefIds.has(renderId),
                                renderStatus,
                                renderDuration,
                                renderDisplay.pluginName,
                                renderDisplay.comment,
                            ),
                        }),
                    });
                }

                if (!isCursorOnLine && isBlockRender) {
                    if (renderStatus !== "idle" && renderStatus !== "cached") {
                        decos.push({
                            from: to,
                            to,
                            deco: Decoration.widget({
                                widget: getCachedStatusDotWidget(renderStatus),
                                side: 1,
                            }),
                        });
                    }
                    if (renderStatus !== "idle") {
                        decos.push({
                            from: to,
                            to,
                            deco: Decoration.widget({
                                widget: getCachedStatusBadgeWidget(renderStatus, renderDuration),
                                side: 1,
                            }),
                        });
                    }
                }
            }
        }

        // $ref{...}  — can appear anywhere (skip inside code spans, output fences, and markdown code fences)
        // L0: pretty pill widget when cursor not on line; L2: status-aware mark when cursor on line
        // Fast skip: avoid matchAll + regex overhead on lines without $ref
        // Output fences contain opaque serialized data — $ref patterns there are literal text, not live references.
        // Markdown fenced blocks (``` / ~~~) — example/documentation text, also literal.
        if (text.includes("$ref{") && !insideOutputFence && !mdFenceLines.has(i)) {
            const isCursorOnRefLine = forceL2 || activeCursorLines.has(i);
            const keepHeadingPreviewInline =
                !isCursorOnRefLine && isMarkdownHeadingLine(text);
            let addedHeadingPreviewLineClass = false;
            for (const m of text.matchAll(REF_RE)) {
                if (m.index == null) continue;
                if (isInsideCodeSpan(text, m.index)) continue;
                if (
                    rangeContainsMarkdownLink(renderedMarkdownLinkMatches, m.index)
                ) {
                    continue;
                }
                if (rangeContainsMarkdownMath(markdownMathMatches, m.index)) {
                    continue;
                }
                if (rangeContainsMarkdownRichInline(renderedMarkdownRichInlineMatches, m.index)) {
                    continue;
                }
                const from = line.from + m.index;
                const to = from + m[0].length;
                const refContent = m[1];
                const info = resolveRef(refContent, forDepth > 0, refCtx);

                if (keepHeadingPreviewInline && !addedHeadingPreviewLineClass) {
                    decos.push({
                        from: line.from,
                        to: line.from,
                        deco: Decoration.line({
                            class: "cm-magic-ref-heading-line",
                        }),
                    });
                    addedHeadingPreviewLineClass = true;
                }

                // Resolve definition ID for execution status lookup
                const refDotIdx = refContent.indexOf(".");
                const refDefId =
                    refDotIdx === -1 ? refContent : refContent.slice(0, refDotIdx);
                const refExecStatus =
                    info.sourceKind === "for"
                        ? ((forStatuses.get(refDefId) as ExecutionStatus | undefined) ??
                            "idle")
                        : info.sourceKind === "definition"
                            ? (statuses.get(refDefId) ?? "idle")
                            : "idle";
                const refPluginName =
                    info.sourceKind === "definition"
                        ? (defPluginMap.get(refDefId) ?? null)
                        : null;
                const refDuration =
                    info.sourceKind === "for"
                        ? (forDurations.get(refDefId) ?? null)
                        : info.sourceKind === "definition"
                            ? (durations.get(refDefId) ?? null)
                            : null;
                const canShowRefExecutionStatus =
                    (info.sourceKind === "definition" || info.sourceKind === "for") &&
                    info.status !== "loop-var" &&
                    info.status !== "scope-var";

                if (isCursorOnRefLine) {
                    // L2: raw syntax with status-aware mark decoration
                    const mark = getRefStatusMark(refContent, forDepth > 0, refCtx);
                    decos.push({ from, to, deco: mark });
                } else {
                    // L0: pretty pill widget replacing raw $ref{...}
                    decos.push({
                        from,
                        to,
                        deco: Decoration.replace({
                            widget: new PrettyRefWidget(
                                refContent,
                                info.definitionId,
                                info.outputPath,
                                info.status,
                                info.resolvedValue,
                                info.accessor,
                                info.sourceKind,
                                refPluginName,
                                info.isImported,
                                canShowRefExecutionStatus
                                    ? refExecStatus
                                    : "idle",
                                canShowRefExecutionStatus ? refDuration : null,
                            ),
                        }),
                    });
                }

                const refErrorMsg =
                    info.status === "error"
                        ? (errors.get(refDefId) ??
                            getRefStatusMessage("error", info.accessor, info.outputPath))
                        : info.status === "unresolved"
                            ? getRefStatusMessage(
                                "unresolved",
                                info.accessor,
                                info.outputPath,
                            )
                            : null;
                if (refErrorMsg) {
                    decos.push({
                        from: to,
                        to,
                        deco: Decoration.widget({
                            widget: new ErrorMessageWidget(refDefId, refErrorMsg),
                            side: 1,
                        }),
                    });
                }
            }
        } // end if text.includes("$ref{")
    }

    // Parse diagnostics — show error widgets on flagged lines
    // Skip diagnostics for lines inside an active (L2) meta block to avoid
    // showing transient JSON parse errors while the user is still editing.
    const parseDiags = view.state.field(parseDiagnosticsField);
    for (const diag of parseDiags) {
        const lineNum = diag.line; // 1-based
        if (lineNum >= 1 && lineNum <= doc.lines) {
            // Suppress errors inside active meta/content/use blocks (L2 editing mode)
            const mb = metaBlockByLine[lineNum];
            if (mb && activeMetaBlockStarts.has(mb.startLine)) continue;
            const cb = contentBlockByLine[lineNum];
            if (cb && activeContentBlockStarts.has(cb.startLine)) continue;
            const ub = useBlockByLine[lineNum];
            if (ub && activeUseBlockStarts.has(ub.startLine)) continue;

            const line = doc.line(lineNum);
            // Error line background
            decos.push({
                from: line.from,
                to: line.from,
                deco: Decoration.line({
                    class: "cm-magic-define-line cm-magic-define-error",
                }),
            });
            // Error message widget after the line
            decos.push({
                from: line.to,
                to: line.to,
                deco: Decoration.widget({
                    widget: new ErrorMessageWidget(diag.code ?? "syntax", diag.message),
                    side: 1,
                }),
            });
        }
    }

    // Sort by from position, then by the decoration's startSide so
    // line decorations (startSide = -2) come before mark/replace decorations
    // at the same position. RangeSetBuilder requires:
    //   - `from` strictly non-decreasing
    //   - at same `from`, `startSide` strictly non-decreasing
    // Tie-break further on endSide and span length to keep the order
    // deterministic and stable across rebuilds (avoids subtle ordering bugs
    // when multiple widgets/replaces collapse to the same position, e.g. at
    // end of a line containing $ref{...}).
    decos.sort((a, b) => {
        if (a.from !== b.from) return a.from - b.from;
        if (a.deco.startSide !== b.deco.startSide)
            return a.deco.startSide - b.deco.startSide;
        if (a.deco.endSide !== b.deco.endSide)
            return a.deco.endSide - b.deco.endSide;
        return a.to - b.to;
    });

    const builder = new RangeSetBuilder<Decoration>();
    let lastFrom = -1;
    let lastTo = -1;
    let lastStartSide = -Infinity;
    let lastEndSide = -Infinity;
    let lastSpec: unknown = null;
    for (const { from, to, deco } of decos) {
        // Defensive guard: RangeSetBuilder throws hard ("Ranges must be added
        // sorted by `from` position and `startSide`") on any ordering violation.
        // When that throws inside a ViewPlugin update, the editor's decoration
        // state becomes stale and the cursor appears frozen / unresponsive.
        // We pre-check with the same rule the builder uses, log the offending
        // pair (so we can root-cause emission sites), and skip the bad range.
        try {
            if (lastFrom !== -1) {
                const diff = (from - lastTo) || (deco.startSide - lastEndSide);
                const fromOrder = (from - lastFrom) || (deco.startSide - lastStartSide);
                if (diff <= 0 && fromOrder < 0) {
                    if (typeof console !== "undefined") {
                        console.warn(
                            "[magic-syntax] skipping out-of-order decoration",
                            {
                                prev: {
                                    from: lastFrom,
                                    to: lastTo,
                                    startSide: lastStartSide,
                                    endSide: lastEndSide,
                                    spec: lastSpec,
                                },
                                next: {
                                    from,
                                    to,
                                    startSide: deco.startSide,
                                    endSide: deco.endSide,
                                    spec: (deco as { spec?: unknown }).spec,
                                },
                            },
                        );
                    }
                    continue;
                }
            }
            builder.add(from, to, deco);
            lastFrom = from;
            lastTo = to;
            lastStartSide = deco.startSide;
            lastEndSide = deco.endSide;
            lastSpec = (deco as { spec?: unknown }).spec;
        } catch (err) {
            if (typeof console !== "undefined") {
                console.error("[magic-syntax] builder.add threw, skipping", err, {
                    from,
                    to,
                    startSide: deco.startSide,
                    endSide: deco.endSide,
                });
            }
        }
    }
    return builder.finish();
}
